import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';

interface Step {
  id: string;
  text: string;
  checked: boolean;
}

interface ProcessData {
  name: string;
  description: string;
  steps: Step[];
  estimatedTime: string;
  involvedParties: string[];
}

export default function ProcessDetail() {
  const { processId } = useParams<{ processId: string }>();
  
  // Scroll to top when component mounts or processId changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [processId]);

  // Reset submitted state when processId changes
  useEffect(() => {
    setSubmitted(false);
    setIsSubmitting(false);
    setSubmissionProgress(0);
  }, [processId]);
  
  // Comprehensive process database with unique steps for each process
  const processDatabase: Record<string, ProcessData> = {
    'course-registration': {
      name: 'Course Registration Process',
      description: 'The process students follow to register for courses each semester',
      estimatedTime: '2-5 days',
      involvedParties: ['Student', 'Academic Advisor', 'Registry', 'Finance Office'],
      steps: [
        { id: '1', text: 'Student receives course registration notification', checked: false },
        { id: '2', text: 'Student meets with academic advisor for course selection', checked: false },
        { id: '3', text: 'Advisor approves course selection and signs registration form', checked: false },
        { id: '4', text: 'Student verifies financial clearance status', checked: false },
        { id: '5', text: 'Student submits registration form to registry', checked: false },
        { id: '6', text: 'Registry validates course availability and prerequisites', checked: false },
        { id: '7', text: 'System generates course schedule and confirmation', checked: false }
      ]
    },
    'hall-residence': {
      name: 'Hall of Residence Process (On-Campus & Off-Campus)',
      description: 'The process for applying and securing accommodation in university halls or approved off-campus residences',
      estimatedTime: '1-3 weeks',
      involvedParties: ['Student', 'Hall Warden', 'Accommodation Office', 'Finance Office', 'Security'],
      steps: [
        { id: '1', text: 'Student receives accommodation application notification', checked: false },
        { id: '2', text: 'Student completes accommodation preference form (on-campus or off-campus)', checked: false },
        { id: '3', text: 'Student verifies financial clearance for accommodation fees', checked: false },
        { id: '4', text: 'Student submits application to accommodation office', checked: false },
        { id: '5', text: 'Accommodation office reviews application and checks availability', checked: false },
        { id: '6', text: 'For off-campus: Student submits landlord details and property verification documents', checked: false },
        { id: '7', text: 'For off-campus: Security office conducts property inspection and approval', checked: false },
        { id: '8', text: 'Student pays accommodation fees', checked: false },
        { id: '9', text: 'Accommodation office allocates room/approves off-campus residence', checked: false },
        { id: '10', text: 'Hall warden issues room key or off-campus approval letter', checked: false },
        { id: '11', text: 'Student completes check-in process and signs accommodation agreement', checked: false }
      ]
    },
    'staff-quarters': {
      name: 'Staff Quarters Allocation Process',
      description: 'The process for allocating on-campus housing to eligible staff members',
      estimatedTime: '2-6 weeks',
      involvedParties: ['Staff Member', 'HR Department', 'Estate Office', 'Finance Office', 'Maintenance Team'],
      steps: [
        { id: '1', text: 'Staff member submits quarters allocation request to HR', checked: false },
        { id: '2', text: 'HR verifies staff eligibility based on grade and employment status', checked: false },
        { id: '3', text: 'HR forwards approved request to estate office', checked: false },
        { id: '4', text: 'Estate office checks availability of suitable quarters', checked: false },
        { id: '5', text: 'Estate office inspects and prepares quarters for occupation', checked: false },
        { id: '6', text: 'Maintenance team conducts necessary repairs and cleaning', checked: false },
        { id: '7', text: 'Estate office notifies staff of quarters allocation', checked: false },
        { id: '8', text: 'Staff member reviews quarters allocation terms and conditions', checked: false },
        { id: '9', text: 'Finance office processes quarters deduction setup from salary', checked: false },
        { id: '10', text: 'Staff member signs occupancy agreement', checked: false },
        { id: '11', text: 'Estate office issues keys and completes handover', checked: false },
        { id: '12', text: 'Staff member completes move-in inspection checklist', checked: false }
      ]
    },
    'exeat-request': {
      name: 'Exeat Request Process (Short & Long Distance)',
      description: 'The process for students to request permission to leave campus temporarily for short or long distance travel',
      estimatedTime: '1-3 days',
      involvedParties: ['Student', 'Hall Warden', 'Parent/Guardian', 'Security', 'Dean of Students'],
      steps: [
        { id: '1', text: 'Student identifies need to leave campus (medical, family, personal)', checked: false },
        { id: '2', text: 'Student completes exeat request form with travel details', checked: false },
        { id: '3', text: 'Student specifies exeat type: short distance (within state) or long distance (interstate/international)', checked: false },
        { id: '4', text: 'For long distance: Student obtains parent/guardian written consent', checked: false },
        { id: '5', text: 'Student submits exeat form to hall warden', checked: false },
        { id: '6', text: 'Hall warden reviews request and verifies student conduct record', checked: false },
        { id: '7', text: 'For long distance: Dean of Students provides additional approval', checked: false },
        { id: '8', text: 'Hall warden approves and signs exeat form', checked: false },
        { id: '9', text: 'Student collects approved exeat pass', checked: false },
        { id: '10', text: 'Security logs student departure with exeat pass at gate', checked: false },
        { id: '11', text: 'Student returns to campus within approved timeframe', checked: false },
        { id: '12', text: 'Security logs student return and closes exeat record', checked: false }
      ]
    },
    'staff-onboarding': {
      name: 'Staff Onboarding Process',
      description: 'The comprehensive process for integrating new staff members into the institution',
      estimatedTime: '2-4 weeks',
      involvedParties: ['New Staff', 'HR Department', 'IT Department', 'Supervisor', 'Finance Office'],
      steps: [
        { id: '1', text: 'New staff receives offer letter and employment contract', checked: false },
        { id: '2', text: 'Staff completes pre-employment documentation and medical clearance', checked: false },
        { id: '3', text: 'HR creates personnel file and employee record', checked: false },
        { id: '4', text: 'IT department creates email account and system access', checked: false },
        { id: '5', text: 'Finance office sets up payroll and bank details', checked: false },
        { id: '6', text: 'Staff attends institutional orientation program', checked: false },
        { id: '7', text: 'HR issues staff ID card and access credentials', checked: false },
        { id: '8', text: 'Supervisor conducts departmental orientation and role briefing', checked: false },
        { id: '9', text: 'Staff receives office space, equipment, and resources', checked: false },
        { id: '10', text: 'HR schedules probation review meetings and mentorship', checked: false }
      ]
    },
    'fee-payment': {
      name: 'Student Fee Payment Process',
      description: 'The process for paying tuition and other institutional fees via UMIS portal',
      estimatedTime: '1-3 days',
      involvedParties: ['Student', 'Finance Office', 'Bank', 'Registry'],
      steps: [
        { id: '1', text: 'Student logs in to UMIS portal at umis.babcock.edu.ng', checked: false },
        { id: '2', text: 'Student starts registration by selecting hostel, meals, worship center, and courses', checked: false },
        { id: '3', text: 'Student views "Current Charges" to see fees owed', checked: false },
        { id: '4', text: 'Student clicks the green "Go" arrow button to proceed', checked: false },
        { id: '5', text: 'Student selects "Make Payment" option', checked: false },
        { id: '6', text: 'Student chooses payment method: Visa/MasterCard, Verve, or direct bank transfer', checked: false },
        { id: '7', text: 'For card payment: Student enters card details and provides OTP sent to phone', checked: false },
        { id: '8', text: 'For bank transfer: Student pays at designated bank for eTranzact PIN or uses transfer code from portal', checked: false },
        { id: '9', text: 'Payment confirmation reflects in Current Charges section', checked: false },
        { id: '10', text: 'Student requests financial approval (wait time: 15-30 minutes to 24 hours)', checked: false },
        { id: '11', text: 'Student prints payment receipt', checked: false },
        { id: '12', text: 'Student submits courses for advisor approval', checked: false }
      ]
    },
    'grade-change': {
      name: 'Change of Grade Process',
      description: 'The process for correcting or appealing course grades',
      estimatedTime: '2-4 weeks',
      involvedParties: ['Student', 'Course Lecturer', 'HOD', 'Exam Officer', 'Registry'],
      steps: [
        { id: '1', text: 'Student identifies grade discrepancy or error', checked: false },
        { id: '2', text: 'Student submits grade review request to course lecturer', checked: false },
        { id: '3', text: 'Lecturer reviews assessment records and scripts', checked: false },
        { id: '4', text: 'Lecturer completes grade change form if error confirmed', checked: false },
        { id: '5', text: 'HOD reviews and approves grade change request', checked: false },
        { id: '6', text: 'Exam officer verifies documentation and signatures', checked: false },
        { id: '7', text: 'Registry updates student academic record', checked: false },
        { id: '8', text: 'Student receives notification of grade update', checked: false }
      ]
    },
    'staff-payroll': {
      name: 'Staff Payroll Process',
      description: 'The monthly process for processing staff salaries and benefits',
      estimatedTime: '5-7 days',
      involvedParties: ['HR Department', 'Finance Office', 'Bank', 'Staff Member'],
      steps: [
        { id: '1', text: 'HR collects attendance and leave records for the month', checked: false },
        { id: '2', text: 'HR verifies staff status changes (new hires, exits, promotions)', checked: false },
        { id: '3', text: 'HR calculates gross salary, allowances, and deductions', checked: false },
        { id: '4', text: 'HR generates payroll schedule and summary report', checked: false },
        { id: '5', text: 'Finance office reviews and approves payroll', checked: false },
        { id: '6', text: 'Finance initiates bank transfer for salary payments', checked: false },
        { id: '7', text: 'Bank processes bulk salary transfers', checked: false },
        { id: '8', text: 'Staff receives salary credit and payslip', checked: false },
        { id: '9', text: 'HR files payroll records and tax documentation', checked: false }
      ]
    },
    'exam-registration': {
      name: 'Exam Registration Process',
      description: 'The process students follow to register for semester examinations',
      estimatedTime: '3-5 days',
      involvedParties: ['Student', 'Course Lecturer', 'Exam Office', 'Finance Office'],
      steps: [
        { id: '1', text: 'Student receives exam registration notification', checked: false },
        { id: '2', text: 'Student verifies course attendance requirement (75% minimum)', checked: false },
        { id: '3', text: 'Course lecturer confirms attendance eligibility', checked: false },
        { id: '4', text: 'Student checks financial clearance status', checked: false },
        { id: '5', text: 'Student completes online exam registration form', checked: false },
        { id: '6', text: 'Exam office validates registration and generates exam number', checked: false },
        { id: '7', text: 'Student downloads and prints exam admission slip', checked: false }
      ]
    },
    'transcript-request': {
      name: 'Transcript Request Process',
      description: 'The process for requesting official academic transcripts',
      estimatedTime: '1-2 weeks',
      involvedParties: ['Student/Alumni', 'Registry', 'Finance Office', 'Courier Service'],
      steps: [
        { id: '1', text: 'Applicant completes transcript request form', checked: false },
        { id: '2', text: 'Applicant pays transcript processing fee', checked: false },
        { id: '3', text: 'Registry verifies applicant identity and academic records', checked: false },
        { id: '4', text: 'Registry generates official transcript document', checked: false },
        { id: '5', text: 'Registrar signs and stamps transcript', checked: false },
        { id: '6', text: 'Transcript is sealed in official envelope', checked: false },
        { id: '7', text: 'Transcript is dispatched via courier or collected in person', checked: false },
        { id: '8', text: 'Applicant receives delivery confirmation', checked: false }
      ]
    },
    'course-withdrawal': {
      name: 'Course Withdrawal Process',
      description: 'The process for withdrawing from a registered course',
      estimatedTime: '3-7 days',
      involvedParties: ['Student', 'Academic Advisor', 'Course Lecturer', 'Registry'],
      steps: [
        { id: '1', text: 'Student identifies need to withdraw from course', checked: false },
        { id: '2', text: 'Student consults with academic advisor', checked: false },
        { id: '3', text: 'Student completes course withdrawal form', checked: false },
        { id: '4', text: 'Academic advisor reviews and signs withdrawal form', checked: false },
        { id: '5', text: 'Course lecturer acknowledges withdrawal', checked: false },
        { id: '6', text: 'Registry processes withdrawal and updates records', checked: false },
        { id: '7', text: 'Student receives withdrawal confirmation', checked: false }
      ]
    },
    'scholarship-application': {
      name: 'Scholarship Application Process',
      description: 'The process for applying for institutional scholarships and financial aid',
      estimatedTime: '2-4 weeks',
      involvedParties: ['Student', 'Financial Aid Office', 'Academic Advisor', 'Scholarship Committee'],
      steps: [
        { id: '1', text: 'Student receives scholarship opportunity announcement', checked: false },
        { id: '2', text: 'Student reviews eligibility criteria and requirements', checked: false },
        { id: '3', text: 'Student gathers required documents (transcripts, essays, references)', checked: false },
        { id: '4', text: 'Student submits complete scholarship application', checked: false },
        { id: '5', text: 'Financial aid office verifies application completeness', checked: false },
        { id: '6', text: 'Scholarship committee reviews and scores applications', checked: false },
        { id: '7', text: 'Committee conducts interviews if required', checked: false },
        { id: '8', text: 'Committee makes final selection decisions', checked: false },
        { id: '9', text: 'Student receives scholarship award notification', checked: false }
      ]
    },
    'refund-request': {
      name: 'Refund Request Process',
      description: 'The process for requesting refunds for overpayments or withdrawals',
      estimatedTime: '2-6 weeks',
      involvedParties: ['Student', 'Finance Office', 'Registry', 'Bank'],
      steps: [
        { id: '1', text: 'Student identifies refund eligibility (withdrawal, overpayment)', checked: false },
        { id: '2', text: 'Student completes refund request form with bank details', checked: false },
        { id: '3', text: 'Student submits supporting documents (receipts, withdrawal proof)', checked: false },
        { id: '4', text: 'Finance office verifies payment records and eligibility', checked: false },
        { id: '5', text: 'Registry confirms student status and withdrawal date', checked: false },
        { id: '6', text: 'Finance calculates refund amount based on policy', checked: false },
        { id: '7', text: 'Finance director approves refund request', checked: false },
        { id: '8', text: 'Finance initiates bank transfer to student account', checked: false },
        { id: '9', text: 'Student receives refund confirmation and payment', checked: false }
      ]
    },
    'payment-plan': {
      name: 'Payment Plan Setup Process',
      description: 'The process for setting up installment payment plans for tuition fees',
      estimatedTime: '3-5 days',
      involvedParties: ['Student', 'Finance Office', 'Academic Advisor', 'Guarantor'],
      steps: [
        { id: '1', text: 'Student requests payment plan option from finance office', checked: false },
        { id: '2', text: 'Finance office explains payment plan terms and conditions', checked: false },
        { id: '3', text: 'Student completes payment plan application form', checked: false },
        { id: '4', text: 'Student provides guarantor information and documentation', checked: false },
        { id: '5', text: 'Finance office verifies guarantor eligibility', checked: false },
        { id: '6', text: 'Finance office calculates installment schedule', checked: false },
        { id: '7', text: 'Student and guarantor sign payment plan agreement', checked: false },
        { id: '8', text: 'Finance office activates payment plan in system', checked: false },
        { id: '9', text: 'Student receives payment schedule and reminders', checked: false }
      ]
    },
    'facility-booking': {
      name: 'Facility Booking Process',
      description: 'The process for booking university facilities for events and activities',
      estimatedTime: '1-2 weeks',
      involvedParties: ['Student/Staff', 'Facilities Office', 'Security', 'Finance Office'],
      steps: [
        { id: '1', text: 'User identifies facility need and checks availability', checked: false },
        { id: '2', text: 'User completes facility booking request form', checked: false },
        { id: '3', text: 'User submits event details and expected attendance', checked: false },
        { id: '4', text: 'Facilities office reviews request and checks calendar', checked: false },
        { id: '5', text: 'Facilities office provides booking quotation', checked: false },
        { id: '6', text: 'User pays facility booking fee', checked: false },
        { id: '7', text: 'Facilities office confirms booking and issues permit', checked: false },
        { id: '8', text: 'Security is notified of approved event', checked: false },
        { id: '9', text: 'User receives facility access and setup instructions', checked: false }
      ]
    },
    'maintenance-request': {
      name: 'Maintenance Request Process',
      description: 'The process for reporting and resolving facility maintenance issues',
      estimatedTime: '1-7 days',
      involvedParties: ['Reporter', 'Facilities Office', 'Maintenance Team', 'Supervisor'],
      steps: [
        { id: '1', text: 'User identifies maintenance issue (plumbing, electrical, etc.)', checked: false },
        { id: '2', text: 'User submits maintenance request with location and description', checked: false },
        { id: '3', text: 'Facilities office receives and logs request', checked: false },
        { id: '4', text: 'Facilities office assesses urgency and priority', checked: false },
        { id: '5', text: 'Maintenance team is assigned to the request', checked: false },
        { id: '6', text: 'Maintenance team inspects issue and determines solution', checked: false },
        { id: '7', text: 'Maintenance team performs repair or replacement', checked: false },
        { id: '8', text: 'Supervisor verifies work completion and quality', checked: false },
        { id: '9', text: 'User receives notification of completed maintenance', checked: false }
      ]
    },
    'visitor-registration': {
      name: 'Visitor Registration Process',
      description: 'The process for registering and managing campus visitors',
      estimatedTime: '30 minutes',
      involvedParties: ['Visitor', 'Security', 'Host', 'Registry'],
      steps: [
        { id: '1', text: 'Visitor arrives at campus gate', checked: false },
        { id: '2', text: 'Security requests visitor identification', checked: false },
        { id: '3', text: 'Visitor provides purpose of visit and host contact', checked: false },
        { id: '4', text: 'Security contacts host to confirm visit', checked: false },
        { id: '5', text: 'Visitor completes registration form with personal details', checked: false },
        { id: '6', text: 'Security issues visitor pass or tag', checked: false },
        { id: '7', text: 'Visitor proceeds to destination with pass', checked: false },
        { id: '8', text: 'Visitor returns pass to security upon exit', checked: false }
      ]
    },
    'parking-permit': {
      name: 'Parking Permit Process',
      description: 'The process for obtaining campus parking permits',
      estimatedTime: '3-5 days',
      involvedParties: ['Applicant', 'Security Office', 'Finance Office', 'Registry'],
      steps: [
        { id: '1', text: 'Applicant requests parking permit application form', checked: false },
        { id: '2', text: 'Applicant completes form with vehicle details', checked: false },
        { id: '3', text: 'Applicant submits vehicle registration and insurance documents', checked: false },
        { id: '4', text: 'Security office verifies applicant eligibility', checked: false },
        { id: '5', text: 'Applicant pays parking permit fee', checked: false },
        { id: '6', text: 'Security office processes permit and assigns parking zone', checked: false },
        { id: '7', text: 'Security office issues parking permit sticker', checked: false },
        { id: '8', text: 'Applicant affixes permit to vehicle windshield', checked: false }
      ]
    },
    'research-proposal': {
      name: 'Research Proposal Approval Process',
      description: 'The process for submitting and approving research proposals',
      estimatedTime: '2-4 weeks',
      involvedParties: ['Researcher', 'Supervisor', 'HOD', 'Research Committee', 'Ethics Board'],
      steps: [
        { id: '1', text: 'Researcher develops research concept and objectives', checked: false },
        { id: '2', text: 'Researcher prepares detailed proposal document', checked: false },
        { id: '3', text: 'Supervisor reviews and provides feedback on proposal', checked: false },
        { id: '4', text: 'Researcher revises proposal based on feedback', checked: false },
        { id: '5', text: 'Supervisor endorses proposal for submission', checked: false },
        { id: '6', text: 'HOD reviews proposal for departmental alignment', checked: false },
        { id: '7', text: 'Research committee evaluates proposal methodology', checked: false },
        { id: '8', text: 'Ethics board reviews for ethical compliance', checked: false },
        { id: '9', text: 'Researcher receives approval or revision requests', checked: false }
      ]
    },
    'grant-application': {
      name: 'Grant Application Process',
      description: 'The process for applying for research grants and funding',
      estimatedTime: '4-8 weeks',
      involvedParties: ['Researcher', 'Research Office', 'Finance Office', 'Grant Committee'],
      steps: [
        { id: '1', text: 'Researcher identifies grant opportunity', checked: false },
        { id: '2', text: 'Researcher reviews grant requirements and eligibility', checked: false },
        { id: '3', text: 'Researcher prepares grant proposal and budget', checked: false },
        { id: '4', text: 'Research office reviews proposal for compliance', checked: false },
        { id: '5', text: 'Finance office reviews and approves budget', checked: false },
        { id: '6', text: 'Authorized signatory endorses application', checked: false },
        { id: '7', text: 'Research office submits application to funder', checked: false },
        { id: '8', text: 'Funder reviews and evaluates application', checked: false },
        { id: '9', text: 'Researcher receives grant decision notification', checked: false }
      ]
    },
    'partnership-agreement': {
      name: 'Partnership Agreement Process',
      description: 'The process for establishing institutional partnerships and MOUs',
      estimatedTime: '4-12 weeks',
      involvedParties: ['Initiator', 'Partner Organization', 'Legal Office', 'Management', 'Registry'],
      steps: [
        { id: '1', text: 'Initiator identifies potential partnership opportunity', checked: false },
        { id: '2', text: 'Initiator prepares partnership concept note', checked: false },
        { id: '3', text: 'Management reviews and approves partnership pursuit', checked: false },
        { id: '4', text: 'Both parties negotiate partnership terms and scope', checked: false },
        { id: '5', text: 'Legal office drafts partnership agreement or MOU', checked: false },
        { id: '6', text: 'Both parties review and provide feedback on draft', checked: false },
        { id: '7', text: 'Legal office finalizes agreement with revisions', checked: false },
        { id: '8', text: 'Authorized signatories from both parties sign agreement', checked: false },
        { id: '9', text: 'Registry archives signed agreement and activates partnership', checked: false }
      ]
    },
    'ethics-approval': {
      name: 'Ethics Approval Process',
      description: 'The process for obtaining ethical clearance for research involving human subjects',
      estimatedTime: '3-6 weeks',
      involvedParties: ['Researcher', 'Ethics Committee', 'Supervisor', 'Research Office'],
      steps: [
        { id: '1', text: 'Researcher completes ethics application form', checked: false },
        { id: '2', text: 'Researcher prepares informed consent documents', checked: false },
        { id: '3', text: 'Researcher submits application with research protocol', checked: false },
        { id: '4', text: 'Ethics committee reviews application for compliance', checked: false },
        { id: '5', text: 'Committee requests clarifications or revisions if needed', checked: false },
        { id: '6', text: 'Researcher addresses committee concerns and resubmits', checked: false },
        { id: '7', text: 'Ethics committee conducts final review', checked: false },
        { id: '8', text: 'Committee issues ethics approval certificate', checked: false },
        { id: '9', text: 'Researcher receives approval to commence research', checked: false }
      ]
    },
    'leave-application': {
      name: 'Leave Application Process',
      description: 'The process for staff to apply for various types of leave',
      estimatedTime: '3-7 days',
      involvedParties: ['Staff Member', 'Supervisor', 'HOD', 'HR Department'],
      steps: [
        { id: '1', text: 'Staff member identifies leave need and checks entitlement', checked: false },
        { id: '2', text: 'Staff member completes leave application form', checked: false },
        { id: '3', text: 'Staff member submits application to immediate supervisor', checked: false },
        { id: '4', text: 'Supervisor reviews application and workload impact', checked: false },
        { id: '5', text: 'Supervisor approves or requests alternative dates', checked: false },
        { id: '6', text: 'HOD provides final approval', checked: false },
        { id: '7', text: 'HR department records leave in system', checked: false },
        { id: '8', text: 'Staff member receives leave approval confirmation', checked: false }
      ]
    },
    'recruitment': {
      name: 'Staff Recruitment Process',
      description: 'The process for recruiting and hiring new staff members',
      estimatedTime: '6-12 weeks',
      involvedParties: ['HR Department', 'Hiring Manager', 'Interview Panel', 'Management'],
      steps: [
        { id: '1', text: 'Department identifies staffing need and prepares requisition', checked: false },
        { id: '2', text: 'HR reviews requisition and approves recruitment', checked: false },
        { id: '3', text: 'HR prepares and publishes job advertisement', checked: false },
        { id: '4', text: 'HR receives and screens applications', checked: false },
        { id: '5', text: 'HR shortlists candidates and schedules interviews', checked: false },
        { id: '6', text: 'Interview panel conducts candidate interviews', checked: false },
        { id: '7', text: 'Panel selects preferred candidate and recommends hire', checked: false },
        { id: '8', text: 'HR conducts reference checks and verification', checked: false },
        { id: '9', text: 'HR prepares offer letter and employment contract', checked: false },
        { id: '10', text: 'Candidate accepts offer and completes onboarding', checked: false }
      ]
    },
    'performance-review': {
      name: 'Performance Review Process',
      description: 'The annual process for evaluating staff performance',
      estimatedTime: '2-4 weeks',
      involvedParties: ['Staff Member', 'Supervisor', 'HOD', 'HR Department'],
      steps: [
        { id: '1', text: 'HR announces performance review period', checked: false },
        { id: '2', text: 'Staff member completes self-assessment form', checked: false },
        { id: '3', text: 'Supervisor reviews staff performance against objectives', checked: false },
        { id: '4', text: 'Supervisor completes performance evaluation form', checked: false },
        { id: '5', text: 'Supervisor conducts performance review meeting with staff', checked: false },
        { id: '6', text: 'Staff and supervisor agree on performance rating', checked: false },
        { id: '7', text: 'Staff and supervisor set objectives for next period', checked: false },
        { id: '8', text: 'HOD reviews and endorses performance rating', checked: false },
        { id: '9', text: 'HR records performance review in personnel file', checked: false }
      ]
    },
    'promotion': {
      name: 'Staff Promotion Process',
      description: 'The process for promoting staff to higher positions or grades',
      estimatedTime: '4-8 weeks',
      involvedParties: ['Staff Member', 'Supervisor', 'HOD', 'HR Department', 'Promotion Committee'],
      steps: [
        { id: '1', text: 'Staff member meets promotion eligibility criteria', checked: false },
        { id: '2', text: 'Staff member submits promotion application with portfolio', checked: false },
        { id: '3', text: 'Supervisor provides recommendation and assessment', checked: false },
        { id: '4', text: 'HOD reviews application and provides endorsement', checked: false },
        { id: '5', text: 'HR verifies eligibility and completeness of application', checked: false },
        { id: '6', text: 'Promotion committee reviews all applications', checked: false },
        { id: '7', text: 'Committee conducts interviews if required', checked: false },
        { id: '8', text: 'Committee makes promotion recommendations', checked: false },
        { id: '9', text: 'Management approves promotions', checked: false },
        { id: '10', text: 'HR issues promotion letters and updates records', checked: false }
      ]
    },
    'salary-advance': {
      name: 'Salary Advance Request Process',
      description: 'The process for staff to request advance payment on their salary',
      estimatedTime: '3-7 days',
      involvedParties: ['Staff Member', 'Supervisor', 'HR Department', 'Finance Office'],
      steps: [
        { id: '1', text: 'Staff member identifies financial need for salary advance', checked: false },
        { id: '2', text: 'Staff member checks advance eligibility and maximum amount', checked: false },
        { id: '3', text: 'Staff member completes salary advance request form', checked: false },
        { id: '4', text: 'Staff member provides justification for advance request', checked: false },
        { id: '5', text: 'Staff member submits request to immediate supervisor', checked: false },
        { id: '6', text: 'Supervisor reviews request and provides recommendation', checked: false },
        { id: '7', text: 'HR department verifies staff employment status and salary', checked: false },
        { id: '8', text: 'HR checks for existing advances or outstanding loans', checked: false },
        { id: '9', text: 'HR calculates advance amount and repayment schedule', checked: false },
        { id: '10', text: 'Finance office reviews and approves advance request', checked: false },
        { id: '11', text: 'Finance office processes advance payment', checked: false },
        { id: '12', text: 'Staff member receives advance via bank transfer', checked: false },
        { id: '13', text: 'HR updates payroll system for automatic deduction', checked: false }
      ]
    },
    'honorarium-payment': {
      name: 'Honorarium Payment Process',
      description: 'The process for processing honorarium payments for additional services rendered',
      estimatedTime: '2-4 weeks',
      involvedParties: ['Staff Member', 'Supervisor', 'HR Department', 'Finance Office', 'Accounts'],
      steps: [
        { id: '1', text: 'Staff member completes additional assignment or service', checked: false },
        { id: '2', text: 'Supervisor verifies completion of assignment', checked: false },
        { id: '3', text: 'Staff member completes honorarium claim form', checked: false },
        { id: '4', text: 'Staff member attaches supporting documents (assignment letter, report)', checked: false },
        { id: '5', text: 'Supervisor endorses honorarium claim', checked: false },
        { id: '6', text: 'HR department receives and reviews claim', checked: false },
        { id: '7', text: 'HR verifies honorarium rate and calculation', checked: false },
        { id: '8', text: 'HR checks compliance with honorarium policy', checked: false },
        { id: '9', text: 'Finance office reviews claim for budget availability', checked: false },
        { id: '10', text: 'Finance office approves payment', checked: false },
        { id: '11', text: 'Accounts processes tax deductions if applicable', checked: false },
        { id: '12', text: 'Accounts initiates bank transfer', checked: false },
        { id: '13', text: 'Staff member receives honorarium payment', checked: false },
        { id: '14', text: 'HR files honorarium records for tax reporting', checked: false }
      ]
    },
    'overtime-claim': {
      name: 'Overtime Claim Process',
      description: 'The process for claiming payment for overtime work',
      estimatedTime: '2-3 weeks',
      involvedParties: ['Staff Member', 'Supervisor', 'HR Department', 'Finance Office'],
      steps: [
        { id: '1', text: 'Staff member works beyond normal working hours', checked: false },
        { id: '2', text: 'Staff member records overtime hours worked', checked: false },
        { id: '3', text: 'Staff member completes overtime claim form', checked: false },
        { id: '4', text: 'Staff member provides details of overtime work performed', checked: false },
        { id: '5', text: 'Supervisor verifies overtime hours and work performed', checked: false },
        { id: '6', text: 'Supervisor approves overtime claim', checked: false },
        { id: '7', text: 'HR department receives approved claim', checked: false },
        { id: '8', text: 'HR verifies staff overtime eligibility', checked: false },
        { id: '9', text: 'HR calculates overtime payment based on hourly rate', checked: false },
        { id: '10', text: 'Finance office reviews and approves payment', checked: false },
        { id: '11', text: 'Accounts processes overtime payment', checked: false },
        { id: '12', text: 'Staff member receives overtime payment via bank transfer', checked: false },
        { id: '13', text: 'HR updates overtime records', checked: false }
      ]
    },
    'staff-loan': {
      name: 'Staff Loan Application Process',
      description: 'The process for staff to apply for institutional loans',
      estimatedTime: '3-6 weeks',
      involvedParties: ['Staff Member', 'Supervisor', 'HR Department', 'Finance Office', 'Loan Committee', 'Guarantor'],
      steps: [
        { id: '1', text: 'Staff member identifies loan need and reviews loan policy', checked: false },
        { id: '2', text: 'Staff member checks loan eligibility criteria', checked: false },
        { id: '3', text: 'Staff member completes loan application form', checked: false },
        { id: '4', text: 'Staff member provides loan purpose and amount requested', checked: false },
        { id: '5', text: 'Staff member identifies and approaches guarantors', checked: false },
        { id: '6', text: 'Guarantors complete guarantor forms and provide consent', checked: false },
        { id: '7', text: 'Supervisor endorses loan application', checked: false },
        { id: '8', text: 'Staff member submits complete application to HR', checked: false },
        { id: '9', text: 'HR verifies staff employment status and salary', checked: false },
        { id: '10', text: 'HR checks for existing loans and repayment history', checked: false },
        { id: '11', text: 'HR verifies guarantor eligibility and capacity', checked: false },
        { id: '12', text: 'Loan committee reviews application', checked: false },
        { id: '13', text: 'Committee calculates loan amount and repayment schedule', checked: false },
        { id: '14', text: 'Committee approves or rejects loan application', checked: false },
        { id: '15', text: 'Staff member signs loan agreement', checked: false },
        { id: '16', text: 'Finance office disburses loan to staff account', checked: false },
        { id: '17', text: 'HR updates payroll for automatic monthly deductions', checked: false }
      ]
    },
    'end-of-service': {
      name: 'End of Service Benefits Process',
      description: 'The process for processing end of service benefits for departing staff',
      estimatedTime: '4-8 weeks',
      involvedParties: ['Staff Member', 'HR Department', 'Finance Office', 'Accounts', 'Management'],
      steps: [
        { id: '1', text: 'Staff member submits resignation or retirement notice', checked: false },
        { id: '2', text: 'HR department receives exit notification', checked: false },
        { id: '3', text: 'HR verifies staff service years and employment records', checked: false },
        { id: '4', text: 'HR calculates end of service benefits entitlement', checked: false },
        { id: '5', text: 'HR prepares benefits calculation breakdown', checked: false },
        { id: '6', text: 'Staff member completes exit clearance form', checked: false },
        { id: '7', text: 'Staff member obtains clearance from all departments', checked: false },
        { id: '8', text: 'Staff member returns institutional property and assets', checked: false },
        { id: '9', text: 'HR verifies completion of exit clearance', checked: false },
        { id: '10', text: 'HR checks for outstanding loans or advances', checked: false },
        { id: '11', text: 'Finance office reviews benefits calculation', checked: false },
        { id: '12', text: 'Finance office deducts outstanding amounts if any', checked: false },
        { id: '13', text: 'Management approves final benefits payment', checked: false },
        { id: '14', text: 'Accounts processes final payment and tax clearance', checked: false },
        { id: '15', text: 'Staff member receives end of service benefits', checked: false },
        { id: '16', text: 'HR issues service certificate and reference letter', checked: false },
        { id: '17', text: 'HR closes staff personnel file', checked: false }
      ]
    },
    'budget-approval': {
      name: 'Budget Approval Process',
      description: 'The process for preparing and approving departmental budgets',
      estimatedTime: '4-8 weeks',
      involvedParties: ['Department', 'HOD', 'Finance Office', 'Budget Committee', 'Management'],
      steps: [
        { id: '1', text: 'Finance office issues budget preparation guidelines', checked: false },
        { id: '2', text: 'Department prepares draft budget with justifications', checked: false },
        { id: '3', text: 'HOD reviews and approves departmental budget', checked: false },
        { id: '4', text: 'Department submits budget to finance office', checked: false },
        { id: '5', text: 'Finance office consolidates all departmental budgets', checked: false },
        { id: '6', text: 'Budget committee reviews and discusses budget proposals', checked: false },
        { id: '7', text: 'Committee requests revisions or clarifications', checked: false },
        { id: '8', text: 'Finance office prepares final consolidated budget', checked: false },
        { id: '9', text: 'Management approves institutional budget', checked: false },
        { id: '10', text: 'Finance office communicates approved budget allocations', checked: false }
      ]
    },
    'expense-reimbursement': {
      name: 'Expense Reimbursement Process',
      description: 'The process for reimbursing staff for approved expenses',
      estimatedTime: '1-3 weeks',
      involvedParties: ['Staff Member', 'Supervisor', 'Finance Office', 'Accounts'],
      steps: [
        { id: '1', text: 'Staff member incurs approved business expense', checked: false },
        { id: '2', text: 'Staff member collects receipts and supporting documents', checked: false },
        { id: '3', text: 'Staff member completes expense reimbursement form', checked: false },
        { id: '4', text: 'Staff member submits claim with receipts to supervisor', checked: false },
        { id: '5', text: 'Supervisor verifies expense legitimacy and approves', checked: false },
        { id: '6', text: 'Finance office reviews claim for policy compliance', checked: false },
        { id: '7', text: 'Accounts processes payment and initiates transfer', checked: false },
        { id: '8', text: 'Staff member receives reimbursement via bank transfer', checked: false }
      ]
    },
    'vendor-payment': {
      name: 'Vendor Payment Process',
      description: 'The process for processing payments to suppliers and vendors',
      estimatedTime: '1-2 weeks',
      involvedParties: ['Vendor', 'Department', 'Finance Office', 'Accounts', 'Bank'],
      steps: [
        { id: '1', text: 'Vendor delivers goods or completes services', checked: false },
        { id: '2', text: 'Department receives and verifies delivery', checked: false },
        { id: '3', text: 'Vendor submits invoice to department', checked: false },
        { id: '4', text: 'Department verifies invoice against purchase order', checked: false },
        { id: '5', text: 'Department approves invoice for payment', checked: false },
        { id: '6', text: 'Finance office receives approved invoice', checked: false },
        { id: '7', text: 'Finance office verifies budget availability', checked: false },
        { id: '8', text: 'Accounts processes payment and initiates transfer', checked: false },
        { id: '9', text: 'Bank transfers payment to vendor account', checked: false },
        { id: '10', text: 'Vendor receives payment confirmation', checked: false }
      ]
    },
    'financial-reporting': {
      name: 'Financial Reporting Process',
      description: 'The process for preparing periodic financial reports',
      estimatedTime: '2-4 weeks',
      involvedParties: ['Finance Office', 'Accounts', 'Auditor', 'Management'],
      steps: [
        { id: '1', text: 'Finance office collects financial data for reporting period', checked: false },
        { id: '2', text: 'Accounts reconciles all financial transactions', checked: false },
        { id: '3', text: 'Finance office prepares draft financial statements', checked: false },
        { id: '4', text: 'Finance office analyzes variances and trends', checked: false },
        { id: '5', text: 'Internal auditor reviews financial statements', checked: false },
        { id: '6', text: 'Finance office addresses audit queries and revises', checked: false },
        { id: '7', text: 'Finance director approves final financial report', checked: false },
        { id: '8', text: 'Finance office presents report to management', checked: false },
        { id: '9', text: 'Management reviews and approves financial report', checked: false }
      ]
    },
    'purchase-request': {
      name: 'Purchase Request Process',
      description: 'The process for requesting and approving purchases',
      estimatedTime: '1-2 weeks',
      involvedParties: ['Requester', 'Supervisor', 'Procurement Office', 'Finance Office'],
      steps: [
        { id: '1', text: 'Requester identifies need for goods or services', checked: false },
        { id: '2', text: 'Requester completes purchase request form with specifications', checked: false },
        { id: '3', text: 'Requester submits request to immediate supervisor', checked: false },
        { id: '4', text: 'Supervisor reviews need and approves request', checked: false },
        { id: '5', text: 'Procurement office receives approved request', checked: false },
        { id: '6', text: 'Finance office verifies budget availability', checked: false },
        { id: '7', text: 'Procurement office sources quotations from vendors', checked: false },
        { id: '8', text: 'Procurement office selects vendor and issues purchase order', checked: false },
        { id: '9', text: 'Requester receives notification of approved purchase', checked: false }
      ]
    },
    'vendor-selection': {
      name: 'Vendor Selection Process',
      description: 'The process for selecting and approving vendors',
      estimatedTime: '2-4 weeks',
      involvedParties: ['Procurement Office', 'Evaluation Committee', 'Finance Office', 'Management'],
      steps: [
        { id: '1', text: 'Procurement office prepares draft contract', checked: false },
        { id: '2', text: 'Legal office reviews contract terms and conditions', checked: false },
        { id: '3', text: 'Procurement office negotiates terms with vendor', checked: false },
        { id: '4', text: 'Legal office finalizes contract document', checked: false },
        { id: '5', text: 'Management reviews and approves contract', checked: false },
        { id: '6', text: 'Both parties sign contract agreement', checked: false },
        { id: '7', text: 'Procurement office registers contract in system', checked: false },
        { id: '8', text: 'Procurement office monitors contract performance', checked: false },
        { id: '9', text: 'Procurement office manages contract renewals or terminations', checked: false }
      ]
    },
    'asset-registration': {
      name: 'Asset Registration Process',
      description: 'The process for registering and tagging institutional assets',
      estimatedTime: '1-2 weeks',
      involvedParties: ['Asset Officer', 'Department', 'Finance Office', 'Registry'],
      steps: [
        { id: '1', text: 'Department receives new asset (equipment, furniture, etc.)', checked: false },
        { id: '2', text: 'Department notifies asset officer of new asset', checked: false },
        { id: '3', text: 'Asset officer inspects and verifies asset', checked: false },
        { id: '4', text: 'Asset officer records asset details (make, model, serial number)', checked: false },
        { id: '5', text: 'Asset officer assigns unique asset tag number', checked: false },
        { id: '6', text: 'Asset officer affixes asset tag to item', checked: false },
        { id: '7', text: 'Asset officer registers asset in inventory system', checked: false },
        { id: '8', text: 'Finance office updates asset register for accounting', checked: false },
        { id: '9', text: 'Asset officer files asset registration documentation', checked: false }
      ]
    },
    'asset-maintenance': {
      name: 'Asset Maintenance Process',
      description: 'The process for maintaining and servicing institutional assets',
      estimatedTime: '1-4 weeks',
      involvedParties: ['Asset Custodian', 'Asset Officer', 'Maintenance Team', 'Vendor'],
      steps: [
        { id: '1', text: 'Asset officer schedules preventive maintenance', checked: false },
        { id: '2', text: 'Asset custodian reports asset malfunction or breakdown', checked: false },
        { id: '3', text: 'Asset officer assesses maintenance requirement', checked: false },
        { id: '4', text: 'Asset officer determines in-house or external service', checked: false },
        { id: '5', text: 'Maintenance team or vendor performs service', checked: false },
        { id: '6', text: 'Asset officer verifies maintenance completion', checked: false },
        { id: '7', text: 'Asset officer updates maintenance records', checked: false },
        { id: '8', text: 'Asset is returned to custodian for use', checked: false }
      ]
    },
    'inventory-audit': {
      name: 'Inventory Audit Process',
      description: 'The process for conducting periodic inventory audits',
      estimatedTime: '2-4 weeks',
      involvedParties: ['Audit Team', 'Asset Officer', 'Department', 'Management'],
      steps: [
        { id: '1', text: 'Management schedules inventory audit', checked: false },
        { id: '2', text: 'Audit team prepares audit plan and checklist', checked: false },
        { id: '3', text: 'Audit team notifies departments of audit schedule', checked: false },
        { id: '4', text: 'Audit team conducts physical count of assets', checked: false },
        { id: '5', text: 'Audit team verifies asset tags and conditions', checked: false },
        { id: '6', text: 'Audit team reconciles physical count with system records', checked: false },
        { id: '7', text: 'Audit team identifies discrepancies and missing items', checked: false },
        { id: '8', text: 'Departments provide explanations for discrepancies', checked: false },
        { id: '9', text: 'Audit team prepares audit report with recommendations', checked: false },
        { id: '10', text: 'Management reviews report and approves actions', checked: false }
      ]
    },
    'asset-disposal': {
      name: 'Asset Disposal Process',
      description: 'The process for disposing of obsolete or damaged assets',
      estimatedTime: '3-6 weeks',
      involvedParties: ['Department', 'Asset Officer', 'Disposal Committee', 'Finance Office'],
      steps: [
        { id: '1', text: 'Department identifies asset for disposal (obsolete, damaged)', checked: false },
        { id: '2', text: 'Department submits disposal request with justification', checked: false },
        { id: '3', text: 'Asset officer inspects asset and verifies condition', checked: false },
        { id: '4', text: 'Disposal committee reviews disposal request', checked: false },
        { id: '5', text: 'Committee approves disposal method (sale, donation, scrap)', checked: false },
        { id: '6', text: 'Asset officer arranges disposal through approved method', checked: false },
        { id: '7', text: 'Asset officer removes asset from inventory system', checked: false },
        { id: '8', text: 'Finance office updates asset register', checked: false },
        { id: '9', text: 'Asset officer files disposal documentation', checked: false }
      ]
    },
    'internal-audit': {
      name: 'Internal Audit Process',
      description: 'The process for conducting internal audits of departments and processes',
      estimatedTime: '3-6 weeks',
      involvedParties: ['Audit Team', 'Auditee Department', 'Management', 'Audit Committee'],
      steps: [
        { id: '1', text: 'Audit team prepares annual audit plan', checked: false },
        { id: '2', text: 'Audit team notifies department of upcoming audit', checked: false },
        { id: '3', text: 'Audit team conducts preliminary review and risk assessment', checked: false },
        { id: '4', text: 'Audit team performs detailed audit procedures', checked: false },
        { id: '5', text: 'Audit team identifies findings and control weaknesses', checked: false },
        { id: '6', text: 'Audit team discusses findings with department', checked: false },
        { id: '7', text: 'Department provides management responses', checked: false },
        { id: '8', text: 'Audit team prepares audit report with recommendations', checked: false },
        { id: '9', text: 'Management reviews report and approves actions', checked: false },
        { id: '10', text: 'Audit team follows up on implementation of recommendations', checked: false }
      ]
    },
    'risk-assessment': {
      name: 'Risk Assessment Process',
      description: 'The process for identifying and assessing institutional risks',
      estimatedTime: '4-8 weeks',
      involvedParties: ['Risk Officer', 'Department', 'Risk Committee', 'Management'],
      steps: [
        { id: '1', text: 'Risk officer initiates risk assessment exercise', checked: false },
        { id: '2', text: 'Departments identify potential risks in their areas', checked: false },
        { id: '3', text: 'Risk officer consolidates identified risks', checked: false },
        { id: '4', text: 'Risk committee evaluates likelihood and impact of risks', checked: false },
        { id: '5', text: 'Risk committee prioritizes risks using risk matrix', checked: false },
        { id: '6', text: 'Departments develop risk mitigation strategies', checked: false },
        { id: '7', text: 'Risk officer prepares risk register and report', checked: false },
        { id: '8', text: 'Management reviews and approves risk treatment plans', checked: false },
        { id: '9', text: 'Risk officer monitors risk mitigation implementation', checked: false }
      ]
    },
    'compliance-review': {
      name: 'Compliance Review Process',
      description: 'The process for reviewing compliance with regulations and policies',
      estimatedTime: '3-6 weeks',
      involvedParties: ['Compliance Officer', 'Department', 'Legal Office', 'Management'],
      steps: [
        { id: '1', text: 'Compliance officer identifies applicable regulations', checked: false },
        { id: '2', text: 'Compliance officer prepares compliance checklist', checked: false },
        { id: '3', text: 'Departments complete self-assessment questionnaires', checked: false },
        { id: '4', text: 'Compliance officer reviews department responses', checked: false },
        { id: '5', text: 'Compliance officer conducts on-site compliance checks', checked: false },
        { id: '6', text: 'Compliance officer identifies non-compliance issues', checked: false },
        { id: '7', text: 'Legal office reviews compliance findings', checked: false },
        { id: '8', text: 'Departments develop corrective action plans', checked: false },
        { id: '9', text: 'Compliance officer prepares compliance report', checked: false },
        { id: '10', text: 'Management reviews report and approves actions', checked: false }
      ]
    },
    'policy-approval': {
      name: 'Policy Approval Process',
      description: 'The process for developing and approving institutional policies',
      estimatedTime: '6-12 weeks',
      involvedParties: ['Policy Owner', 'Stakeholders', 'Legal Office', 'Management', 'Council'],
      steps: [
        { id: '1', text: 'Policy owner identifies need for new or revised policy', checked: false },
        { id: '2', text: 'Policy owner prepares draft policy document', checked: false },
        { id: '3', text: 'Policy owner consults with relevant stakeholders', checked: false },
        { id: '4', text: 'Legal office reviews policy for legal compliance', checked: false },
        { id: '5', text: 'Policy owner revises draft based on feedback', checked: false },
        { id: '6', text: 'Management reviews and endorses policy', checked: false },
        { id: '7', text: 'Policy is presented to council for approval', checked: false },
        { id: '8', text: 'Council approves policy with or without amendments', checked: false },
        { id: '9', text: 'Policy is published and communicated to institution', checked: false },
        { id: '10', text: 'Policy owner monitors policy implementation', checked: false }
      ]
    },
    'it-support': {
      name: 'IT Support Request Process',
      description: 'The process for requesting and resolving IT support issues',
      estimatedTime: '1-5 days',
      involvedParties: ['User', 'IT Help Desk', 'IT Technician', 'IT Manager'],
      steps: [
        { id: '1', text: 'User encounters IT issue (hardware, software, network)', checked: false },
        { id: '2', text: 'User submits support request via help desk portal or phone', checked: false },
        { id: '3', text: 'Help desk logs ticket and assigns priority', checked: false },
        { id: '4', text: 'Help desk attempts remote troubleshooting', checked: false },
        { id: '5', text: 'IT technician is assigned if remote resolution fails', checked: false },
        { id: '6', text: 'Technician diagnoses issue and performs fix', checked: false },
        { id: '7', text: 'Technician tests solution and confirms resolution', checked: false },
        { id: '8', text: 'User confirms issue is resolved', checked: false },
        { id: '9', text: 'Help desk closes ticket and updates records', checked: false }
      ]
    },
    'system-access': {
      name: 'System Access Request Process',
      description: 'The process for requesting access to institutional systems',
      estimatedTime: '2-5 days',
      involvedParties: ['User', 'Supervisor', 'IT Department', 'System Administrator'],
      steps: [
        { id: '1', text: 'User identifies need for system access', checked: false },
        { id: '2', text: 'User completes system access request form', checked: false },
        { id: '3', text: 'Supervisor reviews and approves access request', checked: false },
        { id: '4', text: 'IT department receives approved request', checked: false },
        { id: '5', text: 'IT verifies user identity and authorization', checked: false },
        { id: '6', text: 'System administrator creates user account', checked: false },
        { id: '7', text: 'System administrator assigns appropriate access rights', checked: false },
        { id: '8', text: 'User receives login credentials and instructions', checked: false },
        { id: '9', text: 'User confirms successful system access', checked: false }
      ]
    },
    'software-deployment': {
      name: 'Software Deployment Process',
      description: 'The process for deploying new software across the institution',
      estimatedTime: '2-8 weeks',
      involvedParties: ['IT Department', 'Vendor', 'User Department', 'IT Manager'],
      steps: [
        { id: '1', text: 'IT department receives software deployment request', checked: false },
        { id: '2', text: 'IT evaluates software requirements and compatibility', checked: false },
        { id: '3', text: 'IT prepares deployment plan and timeline', checked: false },
        { id: '4', text: 'IT sets up test environment and conducts testing', checked: false },
        { id: '5', text: 'IT addresses issues identified during testing', checked: false },
        { id: '6', text: 'IT schedules deployment and notifies users', checked: false },
        { id: '7', text: 'IT deploys software to production environment', checked: false },
        { id: '8', text: 'IT provides user training and documentation', checked: false },
        { id: '9', text: 'IT monitors deployment and provides post-deployment support', checked: false }
      ]
    },
    'data-backup': {
      name: 'Data BackupProcess',
      description: 'The process for backing up institutional data',
      estimatedTime: 'Daily/Weekly',
      involvedParties: ['IT Department', 'System Administrator', 'Data Center'],
      steps: [
        { id: '1', text: 'System administrator schedules automated backup jobs', checked: false },
        { id: '2', text: 'Backup system initiates data backup at scheduled time', checked: false },
        { id: '3', text: 'System backs up critical data to backup storage', checked: false },
        { id: '4', text: 'Backup system verifies backup completion and integrity', checked: false },
        { id: '5', text: 'System generates backup completion report', checked: false },
        { id: '6', text: 'System administrator reviews backup logs for errors', checked: false },
        { id: '7', text: 'System administrator resolves any backup failures', checked: false },
        { id: '8', text: 'Backup data is replicated to offsite location', checked: false },
        { id: '9', text: 'System administrator documents backup status', checked: false }
      ]
    },
    'event-approval': {
      name: 'Event Approval Process',
      description: 'The process for approving institutional events and activities',
      estimatedTime: '1-3 weeks',
      involvedParties: ['Event Organizer', 'Supervisor', 'Events Office', 'Management'],
      steps: [
        { id: '1', text: 'Organizer develops event concept and objectives', checked: false },
        { id: '2', text: 'Organizer completes event proposal form', checked: false },
        { id: '3', text: 'Organizer submits proposal with budget to supervisor', checked: false },
        { id: '4', text: 'Supervisor reviews application and workload impact', checked: false },
        { id: '5', text: 'Supervisor approves or requests alternative dates', checked: false },
        { id: '6', text: 'Events office reviews proposal for feasibility', checked: false },
        { id: '7', text: 'Events office checks calendar for conflicts', checked: false },
        { id: '8', text: 'Management approves event and budget', checked: false },
        { id: '9', text: 'Events office issues event approval letter', checked: false },
        { id: '10', text: 'Organizer proceeds with event planning and execution', checked: false }
      ]
    },
    'publication-request': {
      name: 'Publication Request Process',
      description: 'The process for requesting institutional publications and communications',
      estimatedTime: '1-4 weeks',
      involvedParties: ['Requester', 'Communications Office', 'Reviewer', 'Management'],
      steps: [
        { id: '1', text: 'Researcher develops research concept and objectives', checked: false },
        { id: '2', text: 'Researcher prepares detailed proposal document', checked: false },
        { id: '3', text: 'Supervisor reviews and provides feedback on proposal', checked: false },
        { id: '4', text: 'Researcher revises proposal based on feedback', checked: false },
        { id: '5', text: 'Supervisor endorses proposal for submission', checked: false },
        { id: '6', text: 'HOD reviews proposal for departmental alignment', checked: false },
        { id: '7', text: 'Research committee evaluates proposal methodology', checked: false },
        { id: '8', text: 'Ethics board reviews for ethical compliance', checked: false },
        { id: '9', text: 'Researcher receives approval or revision requests', checked: false }
      ]
    },
    'media-engagement': {
      name: 'Media Engagement Process',
      description: 'The process for managing media inquiries and engagements',
      estimatedTime: '1-3 days',
      involvedParties: ['Media', 'Communications Office', 'Spokesperson', 'Management'],
      steps: [
        { id: '1', text: 'Media outlet submits inquiry or interview request', checked: false },
        { id: '2', text: 'Communications office receives and logs request', checked: false },
        { id: '3', text: 'Communications office assesses request and topic', checked: false },
        { id: '4', text: 'Communications office identifies appropriate spokesperson', checked: false },
        { id: '5', text: 'Management approves media engagement', checked: false },
        { id: '6', text: 'Communications office prepares talking points and briefing', checked: false },
        { id: '7', text: 'Spokesperson conducts interview or provides statement', checked: false },
        { id: '8', text: 'Communications office reviews published content', checked: false },
        { id: '9', text: 'Communications office archives media engagement records', checked: false }
      ]
    },
    'alumni-engagement': {
      name: 'Alumni Engagement Process',
      description: 'The process for engaging and maintaining relationships with alumni',
      estimatedTime: 'Ongoing',
      involvedParties: ['Alumni Office', 'Alumni', 'Communications Office', 'Development Office'],
      steps: [
        { id: '1', text: 'Alumni office maintains updated alumni database', checked: false },
        { id: '2', text: 'Alumni office plans engagement activities and events', checked: false },
        { id: '3', text: 'Communications office prepares alumni communications', checked: false },
        { id: '4', text: 'Alumni office sends invitations and updates to alumni', checked: false },
        { id: '5', text: 'Alumni respond and register for events', checked: false },
        { id: '6', text: 'Alumni office coordinates event logistics', checked: false },
        { id: '7', text: 'Alumni office hosts engagement events', checked: false },
        { id: '8', text: 'Alumni office collects feedback and testimonials', checked: false },
        { id: '9', text: 'Development office identifies opportunities for alumni support', checked: false }
      ]
    },
    'exam-setting': {
      name: 'Exam Setting & Moderation Process',
      description: 'The process for creating, reviewing, and approving examination questions',
      estimatedTime: '3-6 weeks',
      involvedParties: ['Course Lecturer', 'Internal Moderator', 'External Examiner', 'HOD', 'Exam Office'],
      steps: [
        { id: '1', text: 'Course lecturer receives exam setting notification from exam office', checked: false },
        { id: '2', text: 'Lecturer reviews course outline and learning objectives', checked: false },
        { id: '3', text: 'Lecturer prepares draft examination questions covering all topics', checked: false },
        { id: '4', text: 'Lecturer creates marking scheme with point allocation', checked: false },
        { id: '5', text: 'Lecturer submits draft exam to internal moderator', checked: false },
        { id: '6', text: 'Internal moderator reviews questions for clarity and coverage', checked: false },
        { id: '7', text: 'Internal moderator checks alignment with course objectives', checked: false },
        { id: '8', text: 'Moderator provides feedback and requests revisions if needed', checked: false },
        { id: '9', text: 'Lecturer revises exam based on moderator feedback', checked: false },
        { id: '10', text: 'Internal moderator approves revised examination', checked: false },
        { id: '11', text: 'HOD reviews and endorses examination questions', checked: false },
        { id: '12', text: 'Exam is sent to external examiner for final review', checked: false },
        { id: '13', text: 'External examiner evaluates questions and marking scheme', checked: false },
        { id: '14', text: 'External examiner provides comments and approval', checked: false },
        { id: '15', text: 'Exam office receives approved examination', checked: false },
        { id: '16', text: 'Exam office formats and prepares final exam paper', checked: false },
        { id: '17', text: 'Exam office securely stores examination until exam date', checked: false }
      ]
    },
    'course-allocation': {
      name: 'Course Allocation Process',
      description: 'The process for assigning courses to lecturers each semester',
      estimatedTime: '2-4 weeks',
      involvedParties: ['HOD', 'Dean', 'Lecturer', 'Registry', 'Timetable Office'],
      steps: [
        { id: '1', text: 'HOD receives list of courses to be offered for the semester', checked: false },
        { id: '2', text: 'HOD reviews lecturer qualifications and teaching loads', checked: false },
        { id: '3', text: 'HOD prepares draft course allocation matching lecturers to courses', checked: false },
        { id: '4', text: 'HOD considers lecturer preferences and expertise areas', checked: false },
        { id: '5', text: 'HOD submits draft allocation to Dean for review', checked: false },
        { id: '6', text: 'Dean reviews allocation for balance and fairness', checked: false },
        { id: '7', text: 'Dean approves allocation or requests adjustments', checked: false },
        { id: '8', text: 'HOD communicates course assignments to lecturers', checked: false },
        { id: '9', text: 'Lecturers acknowledge course assignments', checked: false },
        { id: '10', text: 'Registry updates course allocation in system', checked: false },
        { id: '11', text: 'Timetable office uses allocation to create lecture schedule', checked: false }
      ]
    },
    'lecture-timetable': {
      name: 'Lecture Timetable Creation Process',
      description: 'The process for creating and publishing semester lecture schedules',
      estimatedTime: '2-3 weeks',
      involvedParties: ['Timetable Office', 'HOD', 'Dean', 'Registry', 'Facilities Office'],
      steps: [
        { id: '1', text: 'Timetable office receives approved course allocation', checked: false },
        { id: '2', text: 'Timetable office checks classroom and venue availability', checked: false },
        { id: '3', text: 'Timetable office creates draft lecture schedule', checked: false },
        { id: '4', text: 'Timetable office ensures no lecturer has conflicting classes', checked: false },
        { id: '5', text: 'Timetable office optimizes room utilization', checked: false },
        { id: '6', text: 'Draft timetable is sent to HODs for review', checked: false },
        { id: '7', text: 'HODs provide feedback and request changes if needed', checked: false },
        { id: '8', text: 'Timetable office revises schedule based on feedback', checked: false },
        { id: '9', text: 'Dean approves final timetable', checked: false },
        { id: '10', text: 'Timetable office publishes schedule to students and lecturers', checked: false },
        { id: '11', text: 'Registry updates system with timetable information', checked: false }
      ]
    },
    'result-processing': {
      name: 'Result Processing & Approval Process',
      description: 'The process for computing, reviewing, and approving student examination results',
      estimatedTime: '3-6 weeks',
      involvedParties: ['Course Lecturer', 'HOD', 'Dean', 'External Examiner', 'Registry', 'Senate'],
      steps: [
        { id: '1', text: 'Lecturer marks examination scripts and continuous assessments', checked: false },
        { id: '2', text: 'Lecturer computes total scores and assigns grades', checked: false },
        { id: '3', text: 'Lecturer prepares result sheet with student scores', checked: false },
        { id: '4', text: 'Lecturer submits result sheet to HOD', checked: false },
        { id: '5', text: 'HOD reviews results for consistency and accuracy', checked: false },
        { id: '6', text: 'HOD forwards results to external examiner', checked: false },
        { id: '7', text: 'External examiner reviews sample scripts and result distribution', checked: false },
        { id: '8', text: 'External examiner provides comments and approval', checked: false },
        { id: '9', text: 'Results are presented at departmental board meeting', checked: false },
        { id: '10', text: 'Department board approves results', checked: false },
        { id: '11', text: 'Results are forwarded to faculty board', checked: false },
        { id: '12', text: 'Faculty board reviews and approves results', checked: false },
        { id: '13', text: 'Results are submitted to Senate for final approval', checked: false },
        { id: '14', text: 'Senate approves results for publication', checked: false },
        { id: '15', text: 'Registry publishes approved results to students', checked: false }
      ]
    },
    'curriculum-review': {
      name: 'Curriculum Review Process',
      description: 'The process for reviewing and updating academic program curricula',
      estimatedTime: '3-6 months',
      involvedParties: ['Department', 'HOD', 'Dean', 'Curriculum Committee', 'Senate', 'NUC'],
      steps: [
        { id: '1', text: 'Department identifies need for curriculum review', checked: false },
        { id: '2', text: 'Department forms curriculum review committee', checked: false },
        { id: '3', text: 'Committee conducts stakeholder consultations', checked: false },
        { id: '4', text: 'Committee reviews current curriculum and identifies gaps', checked: false },
        { id: '5', text: 'Committee researches industry trends and best practices', checked: false },
        { id: '6', text: 'Committee prepares draft revised curriculum', checked: false },
        { id: '7', text: 'Department reviews and provides feedback on draft', checked: false },
        { id: '8', text: 'Committee revises curriculum based on feedback', checked: false },
        { id: '9', text: 'HOD presents revised curriculum to faculty board', checked: false },
        { id: '10', text: 'Faculty board reviews and approves curriculum', checked: false },
        { id: '11', text: 'Curriculum is submitted to Senate for approval', checked: false },
        { id: '12', text: 'Senate approves curriculum', checked: false },
        { id: '13', text: 'Curriculum is submitted to NUC for accreditation', checked: false },
        { id: '14', text: 'NUC reviews and approves curriculum', checked: false },
        { id: '15', text: 'Registry implements approved curriculum', checked: false }
      ]
    },
    'research-grant': {
      name: 'Research Grant Management Process',
      description: 'The process for managing and monitoring research grant funds',
      estimatedTime: 'Ongoing',
      involvedParties: ['Researcher', 'Research Office', 'Finance Office', 'Grant Committee', 'Funder'],
      steps: [
        { id: '1', text: 'Researcher receives grant award notification', checked: false },
        { id: '2', text: 'Research office sets up grant account', checked: false },
        { id: '3', text: 'Finance office transfers initial grant funds', checked: false },
        { id: '4', text: 'Researcher submits budget breakdown and work plan', checked: false },
        { id: '5', text: 'Research office approves spending plan', checked: false },
        { id: '6', text: 'Researcher makes purchases and incurs expenses', checked: false },
        { id: '7', text: 'Researcher submits expense claims with receipts', checked: false },
        { id: '8', text: 'Finance office processes reimbursements', checked: false },
        { id: '9', text: 'Researcher submits quarterly progress reports', checked: false },
        { id: '10', text: 'Research office monitors grant utilization', checked: false },
        { id: '11', text: 'Researcher submits financial reports to funder', checked: false },
        { id: '12', text: 'Research office conducts grant audit', checked: false },
        { id: '13', text: 'Researcher submits final project report', checked: false },
        { id: '14', text: 'Research office closes grant account', checked: false }
      ]
    },
    'research-publication': {
      name: 'Research Publication Support Process',
      description: 'The process for supporting researchers in publishing their work',
      estimatedTime: '2-12 months',
      involvedParties: ['Researcher', 'Research Office', 'Library', 'Journal Editor', 'Publisher'],
      steps: [
        { id: '1', text: 'Researcher completes research manuscript', checked: false },
        { id: '2', text: 'Researcher seeks publication support from research office', checked: false },
        { id: '3', text: 'Research office reviews manuscript for quality', checked: false },
        { id: '4', text: 'Library assists with journal selection and indexing check', checked: false },
        { id: '5', text: 'Research office provides feedback on manuscript', checked: false },
        { id: '6', text: 'Researcher revises manuscript based on feedback', checked: false },
        { id: '7', text: 'Researcher submits manuscript to target journal', checked: false },
        { id: '8', text: 'Journal editor conducts peer review', checked: false },
        { id: '9', text: 'Researcher receives reviewer comments', checked: false },
        { id: '10', text: 'Researcher revises manuscript addressing comments', checked: false },
        { id: '11', text: 'Researcher resubmits revised manuscript', checked: false },
        { id: '12', text: 'Journal accepts manuscript for publication', checked: false },
        { id: '13', text: 'Research office assists with publication fees if needed', checked: false },
        { id: '14', text: 'Article is published and researcher notified', checked: false }
      ]
    },
    'conference-attendance': {
      name: 'Conference Attendance Approval Process',
      description: 'The process for obtaining approval to attend academic conferences',
      estimatedTime: '2-4 weeks',
      involvedParties: ['Staff Member', 'HOD', 'Dean', 'HR Department', 'Finance Office'],
      steps: [
        { id: '1', text: 'Staff member identifies relevant conference', checked: false },
        { id: '2', text: 'Staff member prepares conference attendance proposal', checked: false },
        { id: '3', text: 'Staff member includes conference details and benefits', checked: false },
        { id: '4', text: 'Staff member estimates travel and registration costs', checked: false },
        { id: '5', text: 'Staff member submits proposal to HOD', checked: false },
        { id: '6', text: 'HOD reviews proposal and departmental budget', checked: false },
        { id: '7', text: 'HOD endorses proposal and forwards to Dean', checked: false },
        { id: '8', text: 'Dean reviews and approves conference attendance', checked: false },
        { id: '9', text: 'HR department processes leave application', checked: false },
        { id: '10', text: 'Finance office approves funding and advances', checked: false },
        { id: '11', text: 'Staff member registers for conference', checked: false },
        { id: '12', text: 'Staff member attends conference', checked: false },
        { id: '13', text: 'Staff member submits conference report and receipts', checked: false }
      ]
    },
    'sabbatical-leave': {
      name: 'Sabbatical Leave Application Process',
      description: 'The process for applying for sabbatical leave for research or study',
      estimatedTime: '2-3 months',
      involvedParties: ['Staff Member', 'HOD', 'Dean', 'HR Department', 'Management', 'Host Institution'],
      steps: [
        { id: '1', text: 'Staff member checks sabbatical leave eligibility', checked: false },
        { id: '2', text: 'Staff member identifies host institution or research plan', checked: false },
        { id: '3', text: 'Staff member prepares sabbatical leave proposal', checked: false },
        { id: '4', text: 'Staff member obtains invitation letter from host institution', checked: false },
        { id: '5', text: 'Staff member submits proposal to HOD', checked: false },
        { id: '6', text: 'HOD reviews proposal and departmental coverage plan', checked: false },
        { id: '7', text: 'HOD endorses proposal and forwards to Dean', checked: false },
        { id: '8', text: 'Dean reviews and recommends to management', checked: false },
        { id: '9', text: 'HR department verifies eligibility and service years', checked: false },
        { id: '10', text: 'Management reviews and approves sabbatical leave', checked: false },
        { id: '11', text: 'HR department processes leave documentation', checked: false },
        { id: '12', text: 'Staff member proceeds on sabbatical leave', checked: false },
        { id: '13', text: 'Staff member submits periodic progress reports', checked: false },
        { id: '14', text: 'Staff member returns and submits final sabbatical report', checked: false }
      ]
    },
    'faculty-training': {
      name: 'Faculty Training & Development Process',
      description: 'The process for organizing and participating in faculty development programs',
      estimatedTime: '1-6 months',
      involvedParties: ['HR Department', 'Training Coordinator', 'Faculty Member', 'HOD', 'Management'],
      steps: [
        { id: '1', text: 'HR department conducts training needs assessment', checked: false },
        { id: '2', text: 'HR department identifies training opportunities', checked: false },
        { id: '3', text: 'HR department prepares annual training plan', checked: false },
        { id: '4', text: 'Management approves training plan and budget', checked: false },
        { id: '5', text: 'HR department announces training opportunities to faculty', checked: false },
        { id: '6', text: 'Faculty members apply for training programs', checked: false },
        { id: '7', text: 'HOD endorses faculty training applications', checked: false },
        { id: '8', text: 'HR department selects participants based on criteria', checked: false },
        { id: '9', text: 'HR department organizes training logistics', checked: false },
        { id: '10', text: 'Faculty members attend training program', checked: false },
        { id: '11', text: 'Participants complete training evaluation forms', checked: false },
        { id: '12', text: 'HR department issues training certificates', checked: false },
        { id: '13', text: 'Participants share knowledge with colleagues', checked: false },
        { id: '14', text: 'HR department updates training records', checked: false }
      ]
    },
    'academic-promotion': {
      name: 'Academic Promotion Process',
      description: 'The process for promoting academic staff to higher ranks',
      estimatedTime: '4-8 weeks',
      involvedParties: ['Academic Staff', 'HOD', 'Dean', 'Promotion Committee', 'External Assessors', 'Senate'],
      steps: [
        { id: '1', text: 'Academic staff checks promotion eligibility criteria', checked: false },
        { id: '2', text: 'Staff member prepares promotion application portfolio', checked: false },
        { id: '3', text: 'Staff member compiles teaching, research, and service records', checked: false },
        { id: '4', text: 'Staff member submits application to HOD', checked: false },
        { id: '5', text: 'HOD reviews application and provides recommendation', checked: false },
        { id: '6', text: 'Dean reviews application and forwards to promotion committee', checked: false },
        { id: '7', text: 'Promotion committee reviews application', checked: false },
        { id: '8', text: 'Committee sends application to external assessors', checked: false },
        { id: '9', text: 'External assessors evaluate candidate and provide reports', checked: false },
        { id: '10', text: 'Promotion committee reviews assessor reports', checked: false },
        { id: '11', text: 'Committee conducts interview if required', checked: false },
        { id: '12', text: 'Committee makes promotion recommendation', checked: false },
        { id: '13', text: 'Senate reviews and approves promotions', checked: false },
        { id: '14', text: 'HR department issues promotion letters', checked: false },
        { id: '15', text: 'Registry updates staff records', checked: false }
      ]
    },
    'mou-signing': {
      name: 'MOU Signing Process',
      description: 'The process for drafting and signing Memoranda of Understanding',
      estimatedTime: '2-6 weeks',
      involvedParties: ['Initiator', 'Partner Organization', 'Legal Office', 'Management', 'Registry'],
      steps: [
        { id: '1', text: 'Initiator identifies potential partnership opportunity', checked: false },
        { id: '2', text: 'Initiator prepares concept note for MOU', checked: false },
        { id: '3', text: 'Management reviews and approves MOU pursuit', checked: false },
        { id: '4', text: 'Both parties discuss and agree on MOU terms', checked: false },
        { id: '5', text: 'Legal office drafts MOU document', checked: false },
        { id: '6', text: 'Both parties review draft MOU', checked: false },
        { id: '7', text: 'Legal office incorporates feedback and finalizes MOU', checked: false },
        { id: '8', text: 'Management approves final MOU', checked: false },
        { id: '9', text: 'Signing ceremony is scheduled', checked: false },
        { id: '10', text: 'Authorized signatories sign MOU', checked: false },
        { id: '11', text: 'Registry archives signed MOU', checked: false },
        { id: '12', text: 'Both parties receive certified copies', checked: false }
      ]
    },
    'work-study': {
      name: 'Work Study Program Process',
      description: 'The process for students to apply for and participate in the work study program where they work on campus and earnings are applied to their school fees',
      estimatedTime: '2-4 weeks',
      involvedParties: ['Student', 'Work Study Office', 'Department Supervisor', 'Finance Office', 'Bursary'],
      steps: [
        { id: '1', text: 'Student receives work study program announcement', checked: false },
        { id: '2', text: 'Student checks eligibility criteria (financial need, academic standing)', checked: false },
        { id: '3', text: 'Student completes work study application form', checked: false },
        { id: '4', text: 'Student submits application with supporting documents', checked: false },
        { id: '5', text: 'Work study office reviews application and verifies eligibility', checked: false },
        { id: '6', text: 'Work study office checks available work positions', checked: false },
        { id: '7', text: 'Student is matched with suitable department/unit', checked: false },
        { id: '8', text: 'Department supervisor interviews student', checked: false },
        { id: '9', text: 'Work study office approves placement', checked: false },
        { id: '10', text: 'Student signs work study agreement', checked: false },
        { id: '11', text: 'Student receives work schedule and responsibilities', checked: false },
        { id: '12', text: 'Student begins work and logs hours worked', checked: false },
        { id: '13', text: 'Supervisor verifies and approves work hours monthly', checked: false },
        { id: '14', text: 'Work study office calculates monthly earnings', checked: false },
        { id: '15', text: 'Finance office credits earnings to student fee account', checked: false },
        { id: '16', text: 'Student receives monthly work study statement', checked: false },
        { id: '17', text: 'Bursary updates student financial records', checked: false }
      ]
    },
    'heritage-scholarship': {
      name: 'Heritage Scholarship for Adventist Students',
      description: 'The process for baptized and verified Seventh-day Adventist students to apply for Heritage Scholarship through their local churches and receive financial support',
      estimatedTime: '4-8 weeks',
      involvedParties: ['Student', 'Local Church', 'Conference Office', 'Bursary', 'Finance Office', 'Chaplaincy'],
      steps: [
        { id: '1', text: 'Student receives Heritage Scholarship announcement from Bursary', checked: false },
        { id: '2', text: 'Student verifies eligibility (baptized SDA member, good standing)', checked: false },
        { id: '3', text: 'Student obtains Heritage Scholarship application form', checked: false },
        { id: '4', text: 'Student completes application with personal and academic details', checked: false },
        { id: '5', text: 'Student approaches local church pastor for verification', checked: false },
        { id: '6', text: 'Pastor verifies student baptism status and church membership', checked: false },
        { id: '7', text: 'Pastor completes church verification section of form', checked: false },
        { id: '8', text: 'Local church treasurer endorses application', checked: false },
        { id: '9', text: 'Student submits completed form to local church', checked: false },
        { id: '10', text: 'Local church forwards application to Conference office', checked: false },
        { id: '11', text: 'Conference office reviews and verifies all applications', checked: false },
        { id: '12', text: 'Conference office approves scholarship amounts', checked: false },
        { id: '13', text: 'Conference office sends approved list to university Bursary', checked: false },
        { id: '14', text: 'Bursary receives scholarship list and verifies student enrollment', checked: false },
        { id: '15', text: 'Chaplaincy confirms student spiritual life participation', checked: false },
        { id: '16', text: 'Finance office credits scholarship to student fee account', checked: false },
        { id: '17', text: 'Student receives scholarship award notification', checked: false },
        { id: '18', text: 'Bursary updates student financial records', checked: false }
      ]
    }
  };

  const processData = processDatabase[processId || ''] || {
    name: 'Process Not Found',
    description: 'The requested process could not be found in our catalogue',
    estimatedTime: 'N/A',
    involvedParties: ['N/A'],
    steps: [
      { id: '1', text: 'This process has not been documented yet', checked: false },
      { id: '2', text: 'Please return to the catalogue and select a different process', checked: false }
    ]
  };

  const [steps, setSteps] = useState<Step[]>(processData.steps);
  const [newStepText, setNewStepText] = useState('');
  const [processStart, setProcessStart] = useState('');
  const [slowdownStep, setSlowdownStep] = useState('');
  const [repeats, setRepeats] = useState('');
  const [repeatStep, setRepeatStep] = useState('');
  
  const [ratings, setRatings] = useState({
    ease: 3,
    speed: 3,
    transparency: 3,
    errors: 3,
    overall: 3
  });

  const [feedback, setFeedback] = useState({
    frustration: '',
    shouldNotExist: '',
    shouldBeDigital: '',
    suggestion: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [showAddStep, setShowAddStep] = useState(false);
  const [recommendedProcesses, setRecommendedProcesses] = useState<Array<{ id: string; name: string; icon: string }>>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionProgress, setSubmissionProgress] = useState(0);

  // Reset all form data when processId changes
  useEffect(() => {
    setSteps(processData.steps);
    setNewStepText('');
    setProcessStart('');
    setSlowdownStep('');
    setRepeats('');
    setRepeatStep('');
    setRatings({
      ease: 3,
      speed: 3,
      transparency: 3,
      errors: 3,
      overall: 3
    });
    setFeedback({
      frustration: '',
      shouldNotExist: '',
      shouldBeDigital: '',
      suggestion: ''
    });
    setShowAddStep(false);
    setSubmitted(false);
  }, [processId]);

  // Generate smart recommendations based on user feedback
  const generateRecommendations = () => {
    const allProcessIds = Object.keys(processDatabase);
    const currentProcessId = processId || '';
    
    // Remove current process from recommendations
    const availableProcesses = allProcessIds.filter(id => id !== currentProcessId);
    
    // Create a scoring system based on user feedback
    const scoredProcesses = availableProcesses.map(id => {
      let score = 0;
      const process = processDatabase[id];
      
      // If user rated speed low, recommend faster processes
      if (ratings.speed <= 2) {
        if (process.estimatedTime.includes('1-2') || process.estimatedTime.includes('30 minutes')) {
          score += 3;
        }
      }
      
      // If user mentioned digital, recommend IT-related processes
      if (feedback.shouldBeDigital.toLowerCase().includes('digital') || 
          feedback.shouldBeDigital.toLowerCase().includes('online') ||
          feedback.shouldBeDigital.toLowerCase().includes('system')) {
        if (id.includes('it-') || id.includes('system-') || id.includes('software-')) {
          score += 4;
        }
      }
      
      // If user had transparency issues, recommend governance processes
      if (ratings.transparency <= 2) {
        if (id.includes('audit') || id.includes('compliance') || id.includes('governance')) {
          score += 3;
        }
      }
      
      // If process starts with Student, recommend other student processes
      if (processStart === 'Student') {
        if (id.includes('course-') || id.includes('exam-') || id.includes('grade-') || 
            id.includes('transcript-') || id.includes('fee-') || id.includes('scholarship-')) {
          score += 2;
        }
      }
      
      // If process starts with Staff, recommend other staff processes
      if (processStart === 'Staff') {
        if (id.includes('staff-') || id.includes('leave-') || id.includes('recruitment') || 
            id.includes('performance-') || id.includes('promotion')) {
          score += 2;
        }
      }
      
      // If user mentioned frustration with approvals, recommend streamlined processes
      if (feedback.frustration.toLowerCase().includes('approval') || 
          feedback.frustration.toLowerCase().includes('waiting') ||
          feedback.frustration.toLowerCase().includes('delay')) {
        if (process.estimatedTime.includes('1-2') || process.estimatedTime.includes('1-3')) {
          score += 2;
        }
      }
      
      // Add randomness to avoid same recommendations
      score += Math.random() * 2;
      
      return { id, score, process };
    });
    
    // Sort by score and get top 3
    const topProcesses = scoredProcesses
      .sort((a, b) => b.score - a.score)
      .slice(0, 3);
    
    // Map to recommendation format with icons
    const iconMap: Record<string, string> = {
      'course-registration': 'ri-file-list-3-line',
      'exam-registration': 'ri-file-list-3-line',
      'grade-change': 'ri-edit-line',
      'transcript-request': 'ri-file-text-line',
      'course-withdrawal': 'ri-logout-box-line',
      'fee-payment': 'ri-money-dollar-circle-line',
      'scholarship-application': 'ri-award-line',
      'refund-request': 'ri-refund-2-line',
      'payment-plan': 'ri-calendar-check-line',
      'facility-booking': 'ri-building-2-line',
      'maintenance-request': 'ri-tools-line',
      'visitor-registration': 'ri-user-add-line',
      'parking-permit': 'ri-parking-box-line',
      'hall-residence': 'ri-home-4-line',
      'staff-quarters': 'ri-home-office-line',
      'exeat-request': 'ri-map-pin-line',
      'research-proposal': 'ri-flask-line',
      'grant-application': 'ri-funds-line',
      'partnership-agreement': 'ri-handshake-line',
      'ethics-approval': 'ri-shield-check-line',
      'staff-onboarding': 'ri-user-follow-line',
      'staff-payroll': 'ri-money-dollar-box-line',
      'leave-application': 'ri-calendar-event-line',
      'recruitment': 'ri-user-search-line',
      'performance-review': 'ri-bar-chart-box-line',
      'promotion': 'ri-arrow-up-circle-line',
      'budget-approval': 'ri-pie-chart-line',
      'expense-reimbursement': 'ri-wallet-3-line',
      'vendor-payment': 'ri-bank-card-line',
      'financial-reporting': 'ri-file-chart-line',
      'purchase-request': 'ri-shopping-cart-line',
      'vendor-selection': 'ri-store-line',
      'goods-receipt': 'ri-inbox-line',
      'contract-management': 'ri-file-list-2-line',
      'asset-registration': 'ri-barcode-line',
      'asset-maintenance': 'ri-settings-3-line',
      'inventory-audit': 'ri-file-search-line',
      'asset-disposal': 'ri-delete-bin-line',
      'internal-audit': 'ri-search-eye-line',
      'risk-assessment': 'ri-alert-line',
      'compliance-review': 'ri-checkbox-circle-line',
      'policy-approval': 'ri-file-shield-line',
      'it-support': 'ri-customer-service-line',
      'system-access': 'ri-key-line',
      'software-deployment': 'ri-download-cloud-line',
      'data-backup': 'ri-save-line',
      'event-approval': 'ri-calendar-event-line',
      'publication-request': 'ri-newspaper-line',
      'media-engagement': 'ri-mic-line',
      'alumni-engagement': 'ri-graduation-cap-line',
      'exam-setting': 'ri-file-list-3-line',
      'course-allocation': 'ri-list-check',
      'lecture-timetable': 'ri-flow-chart',
      'result-processing': 'ri-edit-line',
      'curriculum-review': 'ri-file-list-2-line',
      'research-grant': 'ri-funds-line',
      'research-publication': 'ri-newspaper-line',
      'conference-attendance': 'ri-calendar-event-line',
      'sabbatical-leave': 'ri-calendar-check-line',
      'faculty-training': 'ri-customer-service-line',
      'academic-promotion': 'ri-arrow-up-circle-line',
      'mou-signing': 'ri-handshake-line',
      'work-study': 'ri-briefcase-line',
      'heritage-scholarship': 'ri-graduation-cap-line'
    };
    
    return topProcesses.map(({ id, process }) => ({
      id,
      name: process.name,
      icon: iconMap[id] || 'ri-file-line'
    }));
  };

  const toggleStep = (stepId: string) => {
    setSteps(steps.map(step => 
      step.id === stepId ? { ...step, checked: !step.checked } : step
    ));
  };

  const addStep = () => {
    if (newStepText.trim()) {
      setSteps([...steps, {
        id: Date.now().toString(),
        text: newStepText,
        checked: false
      }]);
      setNewStepText('');
      setShowAddStep(false);
    }
  };

  const deleteStep = (stepId: string) => {
    setSteps(steps.filter(step => step.id !== stepId));
  };

  const handleSubmit = () => {
    setIsSubmitting(true);
    setSubmissionProgress(0);

    // Simulate submission progress with fun messages
    const progressSteps = [
      { progress: 20, delay: 300 },
      { progress: 40, delay: 400 },
      { progress: 60, delay: 350 },
      { progress: 80, delay: 400 },
      { progress: 100, delay: 350 }
    ];

    let currentStep = 0;

    const runProgress = () => {
      if (currentStep < progressSteps.length) {
        setTimeout(() => {
          setSubmissionProgress(progressSteps[currentStep].progress);
          currentStep++;
          runProgress();
        }, progressSteps[currentStep].delay);
      } else {
        // Generate recommendations after progress completes
        setTimeout(() => {
          const recommendations = generateRecommendations();
          setRecommendedProcesses(recommendations);
          setSubmitted(true);
          setIsSubmitting(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 500);
      }
    };

    runProgress();
  };

  const getRatingColor = (value: number) => {
    if (value <= 2) return 'text-red-600';
    if (value === 3) return 'text-yellow-600';
    return 'text-green-600';
  };

  const getRatingLabel = (value: number) => {
    if (value === 1) return 'Very Poor';
    if (value === 2) return 'Poor';
    if (value === 3) return 'Average';
    if (value === 4) return 'Good';
    return 'Excellent';
  };

  // Submission Progress Overlay
  if (isSubmitting) {
    const getProgressMessage = () => {
      if (submissionProgress < 30) return 'Analyzing your feedback...';
      if (submissionProgress < 60) return 'Processing your ratings...';
      if (submissionProgress < 90) return 'Generating recommendations...';
      return 'Almost done!';
    };

    const getProgressIcon = () => {
      if (submissionProgress < 30) return 'ri-file-search-line';
      if (submissionProgress < 60) return 'ri-bar-chart-box-line';
      if (submissionProgress < 90) return 'ri-lightbulb-flash-line';
      return 'ri-checkbox-circle-line';
    };

    return (
      <div className="fixed inset-0 bg-gradient-to-br from-[#1C2E5D]/95 to-[#284574]/95 backdrop-blur-sm z-50 flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-2xl p-8 shadow-2xl">
            {/* Animated Icon */}
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="w-20 h-20 bg-gradient-to-br from-[#E1C582] to-[#d4b574] rounded-full flex items-center justify-center animate-pulse">
                  <i className={`${getProgressIcon()} text-4xl text-[#1C2E5D]`}></i>
                </div>
                <div className="absolute -inset-2 bg-[#E1C582]/20 rounded-full animate-ping"></div>
              </div>
            </div>

            {/* Progress Message */}
            <h3 className="text-xl font-bold text-gray-900 text-center mb-3">
              {getProgressMessage()}
            </h3>
            <p className="text-sm text-gray-600 text-center mb-6">
              Thank you for your input!
            </p>

            {/* Progress Bar */}
            <div className="relative h-3 bg-gray-200 rounded-full overflow-hidden mb-4">
              <div 
                className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#1C2E5D] to-[#E1C582] rounded-full transition-all duration-500 ease-out"
                style={{ width: `${submissionProgress}%` }}
              >
                <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
              </div>
            </div>

            {/* Progress Percentage */}
            <div className="flex justify-between items-center">
              <span className="text-sm font-semibold text-gray-700">
                {submissionProgress}%
              </span>
              <div className="flex gap-1">
                {[1, 2, 3].map((i) => (
                  <div 
                    key={i}
                    className="w-2 h-2 bg-[#E1C582] rounded-full animate-bounce"
                    style={{ animationDelay: `${i * 0.15}s` }}
                  ></div>
                ))}
              </div>
            </div>

            {/* Fun Facts */}
            <div className="mt-6 p-4 bg-[#E1C582]/10 rounded-lg border border-[#E1C582]/30">
              <p className="text-xs text-gray-600 text-center">
                <i className="ri-information-line mr-1"></i>
                {submissionProgress < 50 
                  ? 'Your feedback helps improve processes for thousands of users'
                  : 'We\'re finding the best processes for you to review next'
                }
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-white">
        <header className="bg-white border-b border-gray-200">
          <div className="max-w-4xl mx-auto px-6 py-6">
            <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 cursor-pointer">
              <i className="ri-arrow-left-line"></i>
              Back to Catalogue
            </Link>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-6 py-12">
          <div className="text-center mb-12">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
              <i className="ri-check-line text-4xl text-green-600"></i>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Thank you for your feedback!</h2>
            <p className="text-base text-gray-600">Your input helps us improve institutional processes for everyone.</p>
          </div>

          <div className="bg-gradient-to-br from-[#E1C582]/20 to-[#E1C582]/10 border border-[#E1C582]/30 rounded-xl p-8 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Based on your feedback, you might also want to review:
            </h3>
            <div className="space-y-3">
              {recommendedProcesses.map((process) => (
                <Link 
                  key={process.id}
                  to={`/process/${process.id}`}
                  onClick={() => setSubmitted(false)}
                  className="block px-5 py-4 bg-white rounded-lg border border-gray-200 hover:border-[#E1C582] hover:shadow-md transition-all cursor-pointer whitespace-nowrap group"
                >
                  <div className="flex items-center gap-3">
                    <i className={`${process.icon} text-xl text-[#1C2E5D]`}></i>
                    <span className="text-sm font-medium text-gray-900 group-hover:text-[#1C2E5D]">{process.name}</span>
                  </div>
                </Link>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-4 text-center">
              <i className="ri-lightbulb-line mr-1"></i>
              These suggestions are personalized based on your ratings and comments
            </p>
          </div>

          <div className="text-center">
            <Link 
              to="/"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#1C2E5D] text-white text-sm font-medium rounded-lg hover:bg-[#284574] transition-colors cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl"
            >
              <i className="ri-arrow-left-line"></i>
              Return to Catalogue
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <Link to="/" className="inline-flex items-center gap-2 text-xs sm:text-sm text-gray-600 hover:text-[#1C2E5D] cursor-pointer mb-3 sm:mb-4 transition-colors">
            <i className="ri-arrow-left-line"></i>
            Back to Catalogue
          </Link>
          <div className="flex flex-col sm:flex-row items-start justify-between gap-3 sm:gap-4">
            <div className="flex-1">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">{processData.name}</h1>
              <p className="text-xs sm:text-sm text-gray-600">{processData.description}</p>
            </div>
            <div className="flex flex-row sm:flex-col items-start sm:items-end gap-3 sm:gap-2 text-xs sm:text-sm w-full sm:w-auto">
              <div className="flex items-center gap-2 text-gray-600">
                <i className="ri-time-line text-[#1C2E5D]"></i>
                <span>{processData.estimatedTime}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <i className="ri-team-line text-[#1C2E5D]"></i>
                <span>{processData.involvedParties.length} parties</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Process Overview */}
        <div className="bg-gradient-to-r from-[#E1C582]/20 to-[#E1C582]/10 rounded-xl p-4 sm:p-6 mb-6 sm:mb-8 border border-[#E1C582]/30">
          <h3 className="text-xs sm:text-sm font-semibold text-gray-900 mb-3">Involved Parties</h3>
          <div className="flex flex-wrap gap-2">
            {processData.involvedParties.map((party, index) => (
              <span key={index} className="px-2.5 sm:px-3 py-1 sm:py-1.5 bg-white rounded-full text-xs font-medium text-gray-700 border border-[#E1C582]/40">
                {party}
              </span>
            ))}
          </div>
        </div>

        {/* Section A: Process Constituents */}
        <section className="mb-8 sm:mb-12 bg-white rounded-xl p-4 sm:p-8 shadow-sm border border-gray-200">
          <div className="flex items-start gap-2 sm:gap-3 mb-4 sm:mb-6">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#E1C582]/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-list-check text-lg sm:text-xl text-[#1C2E5D]"></i>
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">Process Constituents</h2>
              <p className="text-xs sm:text-sm text-gray-600">Help us understand how this process actually works</p>
            </div>
          </div>
          
          <div className="space-y-2 mb-4 sm:mb-6">
            {steps.map((step, index) => (
              <div 
                key={step.id}
                className={`group flex items-center gap-2 sm:gap-3 px-3 sm:px-5 py-3 sm:py-4 rounded-lg border-2 transition-all ${
                  step.checked 
                    ? 'bg-[#E1C582]/10 border-[#E1C582]' 
                    : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                  <span className="w-5 h-5 sm:w-6 sm:h-6 bg-white rounded-full flex items-center justify-center text-xs font-semibold text-gray-600 border border-gray-300 flex-shrink-0">
                    {index + 1}
                  </span>
                  <label className="flex items-center gap-2 sm:gap-3 flex-1 cursor-pointer min-w-0">
                    <input
                      type="checkbox"
                      checked={step.checked}
                      onChange={() => toggleStep(step.id)}
                      className="w-4 h-4 sm:w-5 sm:h-5 rounded border-gray-300 text-[#1C2E5D] focus:ring-[#1C2E5D] cursor-pointer flex-shrink-0"
                    />
                    <span className={`text-xs sm:text-sm ${step.checked ? 'text-gray-900 font-medium' : 'text-gray-700'}`}>
                      {step.text}
                    </span>
                  </label>
                </div>
                {steps.length > 3 && (
                  <button
                    onClick={() => deleteStep(step.id)}
                    className="opacity-0 group-hover:opacity-100 p-1.5 sm:p-2 text-red-500 hover:bg-red-50 rounded-lg transition-all cursor-pointer flex-shrink-0"
                    title="Remove step"
                  >
                    <i className="ri-delete-bin-line text-base sm:text-lg"></i>
                  </button>
                )}
              </div>
            ))}
          </div>

          {!showAddStep ? (
            <button
              onClick={() => setShowAddStep(true)}
              className="w-full px-4 sm:px-5 py-3 sm:py-4 border-2 border-dashed border-gray-300 rounded-lg text-xs sm:text-sm text-gray-600 hover:border-[#E1C582] hover:text-[#1C2E5D] hover:bg-[#E1C582]/5 transition-all cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
            >
              <i className="ri-add-line text-base sm:text-lg"></i>
              Add another step
            </button>
          ) : (
            <div className="flex gap-2 animate-fadeIn">
              <input
                type="text"
                value={newStepText}
                onChange={(e) => setNewStepText(e.target.value)}
                placeholder="Describe the step..."
                className="flex-1 px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm border-2 border-[#E1C582] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C2E5D] focus:border-transparent"
                onKeyPress={(e) => e.key === 'Enter' && addStep()}
                autoFocus
              />
              <button
                onClick={addStep}
                className="px-4 sm:px-6 py-2.5 sm:py-3 bg-[#1C2E5D] text-white text-xs sm:text-sm font-medium rounded-lg hover:bg-[#284574] transition-colors cursor-pointer whitespace-nowrap shadow-sm"
              >
                <i className="ri-check-line"></i>
              </button>
              <button
                onClick={() => {
                  setShowAddStep(false);
                  setNewStepText('');
                }}
                className="px-4 sm:px-6 py-2.5 sm:py-3 bg-gray-200 text-gray-700 text-xs sm:text-sm font-medium rounded-lg hover:bg-gray-300 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
          )}
        </section>

        {/* Section B: Process Flow Experience */}
        <section className="mb-8 sm:mb-12 bg-white rounded-xl p-4 sm:p-8 shadow-sm border border-gray-200">
          <div className="flex items-start gap-2 sm:gap-3 mb-4 sm:mb-6">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#E1C582]/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-flow-chart text-lg sm:text-xl text-[#1C2E5D]"></i>
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">Process Flow Experience</h2>
              <p className="text-xs sm:text-sm text-gray-600">Help us understand how this process actually works</p>
            </div>
          </div>
          
          <div className="space-y-6 sm:space-y-8">
            <div>
              <p className="text-xs sm:text-sm font-semibold text-gray-900 mb-3 sm:mb-4">Where does this process usually start?</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3">
                {['Student', 'Staff', 'System', 'External'].map((option) => (
                  <label 
                    key={option}
                    className={`flex flex-col items-center justify-center px-3 sm:px-4 py-3 sm:py-4 border-2 rounded-xl cursor-pointer transition-all ${
                      processStart === option 
                        ? 'border-orange-500 bg-orange-50 shadow-md' 
                        : 'border-gray-200 hover:border-orange-300 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="processStart"
                      value={option}
                      checked={processStart === option}
                      onChange={(e) => setProcessStart(e.target.value)}
                      className="sr-only"
                    />
                    <i className={`ri-${option === 'Student' ? 'user' : option === 'Staff' ? 'team' : option === 'System' ? 'computer' : 'global'}-line text-xl sm:text-2xl mb-1.5 sm:mb-2 ${
                      processStart === option ? 'text-orange-500' : 'text-gray-400'
                    }`}></i>
                    <span className={`text-xs sm:text-sm font-medium ${processStart === option ? 'text-orange-900' : 'text-gray-700'}`}>
                      {option}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs sm:text-sm font-semibold text-gray-900 mb-3 sm:mb-4">Where does the process slow down the most?</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
                {steps.map((step, index) => (
                  <label 
                    key={index}
                    className={`flex items-start gap-2 sm:gap-3 px-3 sm:px-4 py-3 border-2 rounded-xl cursor-pointer transition-all ${
                      slowdownStep === step.id
                        ? 'border-orange-500 bg-orange-50 shadow-md' 
                        : 'border-gray-200 hover:border-orange-300 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="slowdownStep"
                      value={step.id}
                      checked={slowdownStep === step.id}
                      onChange={(e) => setSlowdownStep(e.target.value)}
                      className="sr-only"
                    />
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                        slowdownStep === step.id ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-600'
                      }`}>
                        <span className="text-xs font-semibold">{index + 1}</span>
                      </div>
                      <span className={`text-xs leading-tight ${slowdownStep === step.id ? 'text-orange-900 font-medium' : 'text-gray-700'}`}>
                        {step.text}
                      </span>
                    </div>
                    {slowdownStep === step.id && (
                      <i className="ri-time-fill text-base text-orange-500 flex-shrink-0"></i>
                    )}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs sm:text-sm font-semibold text-gray-900 mb-3 sm:mb-4">Does this process ever repeat or go backward?</p>
              <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-3 sm:mb-4">
                {['Yes', 'No'].map((option) => (
                  <label 
                    key={option}
                    className={`flex items-center justify-center px-4 sm:px-6 py-3 sm:py-4 border-2 rounded-xl cursor-pointer transition-all ${
                      repeats === option 
                        ? 'border-[#1C2E5D] bg-[#E1C582]/10 shadow-md' 
                        : 'border-gray-200 hover:border-[#E1C582] hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="repeats"
                      value={option}
                      checked={repeats === option}
                      onChange={(e) => setRepeats(e.target.value)}
                      className="sr-only"
                    />
                    <span className={`text-xs sm:text-sm font-semibold ${repeats === option ? 'text-[#1C2E5D]' : 'text-gray-700'}`}>
                      {option}
                    </span>
                  </label>
                ))}
              </div>
              
              {repeats === 'Yes' && (
                <div className="animate-fadeIn">
                  <input
                    type="text"
                    value={repeatStep}
                    onChange={(e) => setRepeatStep(e.target.value)}
                    placeholder="Which step repeats or goes backward?"
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm border-2 border-[#E1C582] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C2E5D] focus:border-transparent"
                  />
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Section C: Process Rating */}
        <section className="mb-8 sm:mb-12 bg-white rounded-xl p-4 sm:p-8 shadow-sm border border-gray-200">
          <div className="flex items-start gap-2 sm:gap-3 mb-4 sm:mb-6">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#E1C582]/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-star-line text-lg sm:text-xl text-[#1C2E5D]"></i>
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">Process Rating</h2>
              <p className="text-xs sm:text-sm text-gray-600">Rate your experience with this process</p>
            </div>
          </div>
          
          <div className="space-y-4 sm:space-y-6">
            {[
              { key: 'ease', label: 'Ease of use', icon: 'ri-hand-heart-line' },
              { key: 'speed', label: 'Speed', icon: 'ri-speed-line' },
              { key: 'transparency', label: 'Transparency', icon: 'ri-eye-line' },
              { key: 'errors', label: 'Error frequency', icon: 'ri-error-warning-line' },
              { key: 'overall', label: 'Overall experience', icon: 'ri-emotion-line' }
            ].map(({ key, label, icon }) => (
              <div key={key} className="bg-gray-50 rounded-lg p-3 sm:p-5 border border-gray-200">
                <div className="flex items-center justify-between mb-2 sm:mb-3">
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <i className={`${icon} text-base sm:text-lg text-gray-600`}></i>
                    <label className="text-xs sm:text-sm font-semibold text-gray-900">{label}</label>
                  </div>
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <span className={`text-xl sm:text-2xl font-bold ${getRatingColor(ratings[key as keyof typeof ratings])}`}>
                      {ratings[key as keyof typeof ratings]}
                    </span>
                    <span className={`text-xs font-medium hidden sm:inline ${getRatingColor(ratings[key as keyof typeof ratings])}`}>
                      {getRatingLabel(ratings[key as keyof typeof ratings])}
                    </span>
                  </div>
                </div>
                <input
                  type="range"
                  min="1"
                  max="5"
                  value={ratings[key as keyof typeof ratings]}
                  onChange={(e) => setRatings({ ...ratings, [key]: parseInt(e.target.value) })}
                  className="w-full h-2 sm:h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#1C2E5D]"
                  style={{
                    background: `linear-gradient(to right, #1C2E5D 0%, #1C2E5D ${((ratings[key as keyof typeof ratings] - 1) / 4) * 100}%, #e5e7eb ${((ratings[key as keyof typeof ratings] - 1) / 4) * 100}%, #e5e7eb 100%)`
                  }}
                />
                <div className="flex justify-between mt-1.5 sm:mt-2">
                  {[1, 2, 3, 4, 5].map((num) => (
                    <span 
                      key={num}
                      className={`text-xs font-medium cursor-pointer ${
                        ratings[key as keyof typeof ratings] === num ? 'text-[#1C2E5D]' : 'text-gray-400'
                      }`}
                      onClick={() => setRatings({ ...ratings, [key]: num })}
                    >
                      {num}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Section D: Pains & Suggestions */}
        <section className="mb-8 sm:mb-12 bg-white rounded-xl p-4 sm:p-8 shadow-sm border border-gray-200">
          <div className="flex items-start gap-2 sm:gap-3 mb-4 sm:mb-6">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#E1C582]/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-chat-3-line text-lg sm:text-xl text-[#1C2E5D]"></i>
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">Pains & Suggestions</h2>
              <p className="text-xs sm:text-sm text-gray-600">Share your honest feedback to help improve this process</p>
            </div>
          </div>
          
          <div className="space-y-4 sm:space-y-6">
            <div>
              <label className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-semibold text-gray-900 mb-2 sm:mb-3">
                <i className="ri-emotion-unhappy-line text-red-500"></i>
                What frustrates you most about this process?
              </label>
              <textarea
                value={feedback.frustration}
                onChange={(e) => setFeedback({ ...feedback, frustration: e.target.value })}
                rows={4}
                maxLength={500}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C2E5D] focus:border-transparent resize-none"
                placeholder="Share your experience and frustrations..."
              />
              <div className="flex justify-between items-center mt-1.5 sm:mt-2">
                <p className="text-xs text-gray-500">{feedback.frustration.length}/500 characters</p>
                {feedback.frustration.length > 0 && (
                  <span className="text-xs text-[#1C2E5D] font-medium">
                    <i className="ri-check-line"></i> Input recorded
                  </span>
                )}
              </div>
            </div>

            <div>
              <label className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-semibold text-gray-900 mb-2 sm:mb-3">
                <i className="ri-subtract-line text-orange-500"></i>
                Which step should not exist or should be merged?
              </label>
              <textarea
                value={feedback.shouldNotExist}
                onChange={(e) => setFeedback({ ...feedback, shouldNotExist: e.target.value })}
                rows={4}
                maxLength={500}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C2E5D] focus:border-transparent resize-none"
                placeholder="Identify unnecessary or redundant steps..."
              />
              <div className="flex justify-between items-center mt-1.5 sm:mt-2">
                <p className="text-xs text-gray-500">{feedback.shouldNotExist.length}/500 characters</p>
                {feedback.shouldNotExist.length > 0 && (
                  <span className="text-xs text-[#1C2E5D] font-medium">
                    <i className="ri-check-line"></i> Input recorded
                  </span>
                )}
              </div>
            </div>

            <div>
              <label className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-semibold text-gray-900 mb-2 sm:mb-3">
                <i className="ri-smartphone-line text-blue-500"></i>
                Which part should be digital instead of manual?
              </label>
              <textarea
                value={feedback.shouldBeDigital}
                onChange={(e) => setFeedback({ ...feedback, shouldBeDigital: e.target.value })}
                rows={4}
                maxLength={500}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C2E5D] focus:border-transparent resize-none"
                placeholder="Suggest areas for digitization..."
              />
              <div className="flex justify-between items-center mt-1.5 sm:mt-2">
                <p className="text-xs text-gray-500">{feedback.shouldBeDigital.length}/500 characters</p>
                {feedback.shouldBeDigital.length > 0 && (
                  <span className="text-xs text-[#1C2E5D] font-medium">
                    <i className="ri-check-line"></i> Input recorded
                  </span>
                )}
              </div>
            </div>

            <div>
              <label className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-semibold text-gray-900 mb-2 sm:mb-3">
                <i className="ri-lightbulb-line text-yellow-500"></i>
                Any suggestion to improve this process?
              </label>
              <textarea
                value={feedback.suggestion}
                onChange={(e) => setFeedback({ ...feedback, suggestion: e.target.value })}
                rows={4}
                maxLength={500}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C2E5D] focus:border-transparent resize-none"
                placeholder="Share your ideas for improvement..."
              />
              <div className="flex justify-between items-center mt-1.5 sm:mt-2">
                <p className="text-xs text-gray-500">{feedback.suggestion.length}/500 characters</p>
                {feedback.suggestion.length > 0 && (
                  <span className="text-xs text-[#1C2E5D] font-medium">
                    <i className="ri-check-line"></i> Input recorded
                  </span>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Submit Button */}
        <div className="flex flex-col items-center gap-3 sm:gap-4">
          <button
            onClick={handleSubmit}
            className="w-full sm:w-auto px-8 sm:px-12 py-3 sm:py-4 bg-gradient-to-r from-[#1C2E5D] to-[#284574] text-white text-sm sm:text-base font-semibold rounded-lg hover:from-[#284574] hover:to-[#1C2E5D] transition-all cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            <i className="ri-send-plane-fill mr-2"></i>
            Submit Feedback
          </button>
          <p className="text-center text-xs sm:text-sm text-gray-500 flex items-center gap-2 px-4">
            <i className="ri-lock-line text-[#1C2E5D]"></i>
            All feedback is anonymous and helps improve institutional processes
          </p>
        </div>
      </main>
    </div>
  );
}
